import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGuid } from './GuidContext';
import { useDevice } from './useDevice';
import { ColorPalette } from './ColorPalette';
import MobileColorPicker from './MobileColorPicker';
import { jitterHex } from './HsvJitterModal';
import RatingComponent from './MyRatingComponent';
import ConfirmDialog from './ConfirmDialog';
import { captureTimelapseFrame, resetCaptureState } from './timelapse/capture';
import { getTodayCST, purgeSession } from './timelapse/db';

const MAX_LAYERS = 5;

const RATIOS = {
    square:    { w: 1, h: 1 },
    landscape: { w: 3, h: 2 },
    portrait:  { w: 2, h: 3 },
};

const isRatioKey = (k) => k === 'square' || k === 'landscape' || k === 'portrait';

const defaultRatioForDevice = ({ isPhone }) => (isPhone ? 'portrait' : 'landscape');

// Fit the chosen ratio inside (90% × viewport width, 70% × viewport height)
// without distortion. Returns CSS pixel dimensions; bitmap is 2× this.
const computeCanvasSize = (ratioKey) => {
    const { w, h } = RATIOS[ratioKey] || RATIOS.portrait;
    const r = w / h;
    const maxW = window.innerWidth * 0.9;
    const maxH = window.innerHeight * 0.7;
    let desiredWidth, desiredHeight;
    if (maxW / maxH >= r) {
        desiredHeight = maxH;
        desiredWidth  = maxH * r;
    } else {
        desiredWidth  = maxW;
        desiredHeight = maxW / r;
    }
    return { desiredWidth, desiredHeight };
};

// Returns true if `el` is part of an interactive UI control (toolbar, modal,
// input, button, layer panel). Used to exclude touches originating on overlay
// Width of the main canvas's CSS border (kept in sync with canvasStyle.border).
// Subtracted from touch-derived offsets so strokes land under the stylus tip
// even at high zoom levels, where a 2 CSS-pixel border would otherwise show
// up as a 16 screen-pixel offset.
const CANVAS_BORDER = 2;

// UI from being treated as canvas touches — important because the canvas
// extends visually under fixed-position overlays.
const isUIElement = (el) =>
    !!(el && el.closest && (
        el.closest('.palette-z-index') ||
        el.closest('.color-picker-modal') ||
        el.closest('input') ||
        el.closest('button') ||
        el.closest('.layer-selection')
    ));

// ── Selection tool ──
// Sizes are in screen pixels; the overlay drawing code multiplies by
// dpr / pinch-scale so handles and dashes keep the same apparent size at
// any zoom level.
const SEL_HANDLE_SIZE = 8;
// Handle hit radius depends on pointer precision: fingers need a fat target,
// but a mouse or Apple Pencil (touchType 'stylus') is precise — and with 10
// handles competing, the finger radius blankets a small selection's interior
// so "move" would win nowhere.
const SEL_HIT_RADIUS = 22;
const SEL_HIT_RADIUS_PRECISE = 10;
const SEL_ROTATE_DEADZONE = 15;
// Copy handle sits halfway between the center (rotate) handle and the top
// edge, in the selection's unit coords — always inside the box, scaling
// with it. On small selections it crowds the rotate handle; hit-testing
// resolves that nearest-wins.
const SEL_COPY_UY = -0.5;
const SEL_MIN_SIZE_CSS = 4;
const SEL_MIN_SCALE = 0.05;
const SEL_DASH = [6, 4];
// Lasso: releasing within this many screen px of the start point closes the
// path and creates the selection; farther cancels the whole gesture.
const LASSO_CLOSE_TOL_CSS = 20;
// Bitmap px between collected lasso points. Deliberately not zoom-scaled:
// zoomed in, denser bitmap sampling is exactly what tracing fine detail wants.
const LASSO_MIN_SEG = 2;
// The 8 perimeter handles in unit coords (multiplied by w/2, h/2). Corners
// scale both axes, edge midpoints scale one. The 9th (center) handle rotates
// and is treated separately everywhere.
const SEL_HANDLES = [
    { id: 'nw', ux: -1, uy: -1 }, { id: 'n', ux: 0, uy: -1 }, { id: 'ne', ux: 1, uy: -1 },
    { id: 'e', ux: 1, uy: 0 }, { id: 'se', ux: 1, uy: 1 }, { id: 's', ux: 0, uy: 1 },
    { id: 'sw', ux: -1, uy: 1 }, { id: 'w', ux: -1, uy: 0 },
];

const rotVec = (x, y, theta) => {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    return { x: x * c - y * s, y: x * s + y * c };
};

// World (bitmap-px) position of a selection handle given unit coords (±1/0).
const selHandleWorld = (sel, ux, uy) => {
    const r = rotVec(ux * (sel.w / 2) * sel.scaleX, uy * (sel.h / 2) * sel.scaleY, sel.rotation);
    return { x: sel.centerX + r.x, y: sel.centerY + r.y };
};

const selCopyHandleWorld = (sel) => selHandleWorld(sel, 0, SEL_COPY_UY);

const DrawingCanvas = ({
    color,
    setColor,
    mySaveDrawingRef,
    isPublished,
    setIsPublished,
    myShareDrawingRef,
    setShowSubject,
    handedness
}) => {
    const [layers, setLayers] = useState([]);         // Array of { canvasRef, context } objects
    const [activeLayerIndex, setActiveLayerIndex] = useState(0);
    const mainCanvasRef = useRef(null);
    const mainContext = useRef(null);
    const workCanvasRef = useRef(null);
    const workContext = useRef(null);
    const beginDrawLayerCanvasRef = useRef(null);
    const beginDrawLayerContext = useRef(null);
    const offScreenCanvasRef = useRef(null);
    const offScreenContext = useRef(null);

    const { guid } = useGuid();
    const { isPhone, isTablet } = useDevice();
    const navigate = useNavigate();

    const [isDrawing, setIsDrawing] = useState(false);
    const [touchPoints, setTouchPoints] = useState([]);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [brushSize, setBrushSize] = useState(2);
    const [alpha, setAlpha] = useState(1.0);
    const [eraseMode, setEraseMode] = useState(false);
    const [blendingMode, setBlendingMode] = useState(false);
    const [lastPoint, setLastPoint] = useState(null);
    const [dailySubject, setDailySubject] = useState('');
    const [isCanvasModified, setIsCanvasModified] = useState(false);
    const [lineMode, setLineMode] = useState('squiggle');
    const [specialBrushMode, setSpecialBrushMode] = useState(false);
    const [lineStart, setLineStart] = useState({ x: 0, y: 0 });
    const [transformMode, setTransformMode] = useState(false);
    // Synchronous gate so handleTransformStart can no-op when re-entered
    // within the same gesture. handleDocumentTouchMove calls startDrawing on
    // every touchmove if isDrawingRef is false — which it always is during a
    // transform — and without this guard the snapshot would refresh every
    // move event, resetting the gesture anchors each frame.
    const isTransformingRef = useRef(false);
    // ── Selection tool state ──
    const [selectionMode, setSelectionMode] = useState(false);
    // Ref mirror so startDrawing/draw/endDrawing (reached through the
    // document-listener refs) can branch without a stale closure.
    const selectionModeRef = useRef(false);
    // The floating selection: pixels cut off the layer plus their transform.
    // { canvas, w, h, origX, origY, layerIndex,
    //   centerX, centerY, rotation, scaleX, scaleY, transformed, isCopy }
    // isCopy marks a selection spawned from the copy handle: its source is
    // already baked into the layer, so cancel discards instead of restoring.
    // Kept in a ref — transforms mutate it every pointer-move.
    const selectionRef = useRef(null);
    // In-flight pointer gesture on the selection (lasso/move/rotate/scale).
    const selectionDragRef = useRef(null);
    // Same role as isTransformingRef: stops handleDocumentTouchMove (and the
    // mouse-enter re-entry path) from re-firing startDrawing mid-gesture,
    // which would reset the drag anchors every move event.
    const isSelectionInteractingRef = useRef(false);
    // ── Layer HSV adjust sliders (tablet) ──
    // Slider positions. They persist across gestures for the current target
    // (floating selection or active layer) and only re-center when the
    // target changes — see resetAdjustSession.
    const [adjustVals, setAdjustVals] = useState({ h: 0, s: 0, v: 0 });
    // The session: source snapshot + reusable output buffer + content bbox.
    // Created on the first slider change for a target, kept until the target
    // changes. All slider values are absolute offsets from this snapshot.
    const adjustSessionRef = useRef(null);
    const adjustPendingRef = useRef(null);
    const adjustRafRef = useRef(0);
    // Marks the adjuster's own saveHistory call so the invalidation hub
    // inside saveHistory doesn't kill the live session.
    const adjustBakingRef = useRef(false);
    const overlayCanvasRef = useRef(null);
    const overlayContext = useRef(null);
    const [overlayLayout, setOverlayLayout] = useState({ left: 0, top: 0 });
    const touchHoldTimerRef = useRef(null);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [sampleMode, setSampleMode] = useState(false);
    const [hasMoved, setHasMoved] = useState(false);
    const [recentResize, setRecentResize] = useState(false);

    const [scale, setScale] = useState(1);
    // Mirror-flip the main canvas visually around the vertical axis. View-only —
    // a common artist technique for spotting drawing errors. Doesn't modify
    // layer pixels; just flips the display + inverts input X mapping.
    const [flipped, setFlipped] = useState(false);
    const flippedRef = useRef(false);
    const [showSplash, setShowSplash] = useState(false);
    const [showSignaturePrompt, setShowSignaturePrompt] = useState(false);
    const [signature, setSignature] = useState('');
    const [localSignature, setLocalSignature] = useState('');

    const [showConfirm, setShowConfirm] = useState(false);

    // 1) We need a boolean to track if it’s a same-day load
    const [shouldLoad, setShouldLoad] = useState(false);

    const [showLayerMenuIndex, setShowLayerMenuIndex] = useState(null);

    // Right after your other state variables:
    const [layerButtonImages, setLayerButtonImages] = useState([]);


    const [isTransforming, setIsTransforming] = useState(false);


    const [colorHistory, setColorHistory] = useState([]);
    const [isInitialColor, setIsInitialColor] = useState(true); // Track if it's the initial color
    const tempStrokeColorRef = useRef(null);
    const jitterRef = useRef({ h: 0, s: 0, v: 0, mode: 'stroke' });
    const strokeBaseColorRef = useRef(null);
    // For per-paint jitter: tracks the end-point of the last painted segment
    // (== the previous frame's midpoint), so each isolated segment moveTo's
    // there instead of to the raw pointer position — which would leave a
    // half-sample gap between segments and break curve continuity.
    const lastPaintEndRef = useRef(null);
    const handleJitterChange = useCallback((j) => {
        jitterRef.current = j || { h: 0, s: 0, v: 0, mode: 'stroke' };
    }, []);

    const [baseColor, setBaseColor] = useState('#ffffff');
    const [baseColorPickerOpen, setBaseColorPickerOpen] = useState(false);
    const [draftBaseColor, setDraftBaseColor] = useState('#ffffff');

    // Aspect ratio key: 'square' | 'landscape' | 'portrait'.
    // Locked for the lifetime of the day's drawing (persisted in localStorage,
    // cleared with the rest of the daily state on new-day rollover).
    const [canvasRatio, setCanvasRatio] = useState(() => defaultRatioForDevice({ isPhone }));

    const lastUpdateTimeRef = useRef(0);
    const endDrawingRef = useRef(null);
    const drawRef = useRef(null);
    const startDrawingRef = useRef(null);
    const handleCombinedTouchMoveRef = useRef(null);
    const isDrawingRef = useRef(false);
    const pointerDownOnUIRef = useRef(false);
    // True whenever any touch is currently on the screen. Used to gate mouse
    // events that are actually Apple Pencil hover dispatches (iPad Pro M2+,
    // iOS 16+) — those shouldn't influence the canvas while a finger is down.
    const anyTouchActiveRef = useRef(false);

    const [hydrated, setHydrated] = useState(false);


    const updateColorHistory = useCallback((newColor) => {
        // 1) If it's the first color ever, skip any time-check
        if (isInitialColor) {
            setIsInitialColor(false);
        } else {
            const now = Date.now();

            // 2) If less than 10ms since last time we accepted a color, skip
            if (now - lastUpdateTimeRef.current < 300) {
                // Simply return without updating the history
                return;
            }
        }

        // 3) Proceed with your normal logic:
        setColorHistory((prevHistory) => {
            const filteredHistory = prevHistory.filter((c) => c !== newColor);
            return [newColor, ...filteredHistory].slice(0, 10);
        });

        // 4) Record the time we last updated the color history
        lastUpdateTimeRef.current = Date.now();

    }, [isInitialColor]);


    useEffect(() => {
        if (!blendingMode) {
            tempStrokeColorRef.current = null;
        }
    }, [blendingMode]);

    const handleColorChange = (newColor, skipHistory = false) => {

        setColor(newColor);

        // Persist today's selected color (single key)
        localStorage.setItem(selectedColorKey(), newColor);

        if (!skipHistory) {

            updateColorHistory(newColor);
        }
    };

    // Function to handle color selection from history
    const handleColorFromHistory = (selectedColor) => {
        // Deliberately picking a color means the user wants to paint with it,
        // not keep blending canvas colors — drop blend mode like erase/sample do.
        setBlendingMode(false);
        handleColorChange(selectedColor, true); // true => skip color-history update
    };


    const [transformStartData, setTransformStartData] = useState({
        // Original two-finger positions for rotation/scale
        startTouches: [],
        // The midpoint around which we rotate/scale
        anchorX: 0,
        anchorY: 0,
        // Distances/angles at the beginning
        startDistance: 0,
        startAngle: 0,
        // For single-finger or multi-finger translation
        startOffsetX: 0,
        startOffsetY: 0,
    });
    // We track the “current” transform being previewed
    const [transformPreview, setTransformPreview] = useState({
        translateX: 0,
        translateY: 0,
        rotation: 0,
        scale: 1,
    });

    // ADDED: RGB <-> HSV + hex helpers
    const rgbToHex = (r, g, b) => {
        const h = (v) => v.toString(16).padStart(2, '0');
        return `#${h(r)}${h(g)}${h(b)}`;
    };

    const rgbaToHex = (rgbaStr) => {
        const m = rgbaStr.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (!m) return '#000000';
        const [r, g, b] = [m[1], m[2], m[3]].map(n => {
            const h = parseInt(n, 10).toString(16);
            return h.length === 1 ? '0' + h : h;
        });
        return `#${r}${g}${b}`;
    };

    const rgbToHsv = (r, g, b) => {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const d = max - min;
        let h;
        if (d === 0) h = 0;
        else if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h = (h * 60 + 360) % 360;
        const s = max === 0 ? 0 : d / max;
        return { h, s, v: max };
    };

    const hsvToRgb = (h, s, v) => {
        const C = v * s;
        const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
        const m = v - C;
        let r = 0, g = 0, b = 0;
        if (h < 60) { r = C; g = X; }
        else if (h < 120) { r = X; g = C; }
        else if (h < 180) { g = C; b = X; }
        else if (h < 240) { g = X; b = C; }
        else if (h < 300) { r = X; b = C; }
        else { r = C; b = X; }
        return {
            r: Math.round((r + m) * 255),
            g: Math.round((g + m) * 255),
            b: Math.round((b + m) * 255),
        };
    };

    const samplePixelHexAtCssPoint = (xCss, yCss) => {
        const ctx = mainContext.current;
        const canvasEl = mainCanvasRef.current;
        if (!ctx || !canvasEl) return null;

        // Because the canvas element is CSS-transformed (translate/scale),
        // compute the "effective" unscaled CSS size, then map to canvas pixels.
        const rect = canvasEl.getBoundingClientRect();
        const effectiveCssW = rect.width / scale;
        const effectiveCssH = rect.height / scale;

        const scaleX = ctx.canvas.width / effectiveCssW;
        const scaleY = ctx.canvas.height / effectiveCssH;

        const px = Math.max(0, Math.min(ctx.canvas.width - 1, Math.round(xCss * scaleX)));
        const py = Math.max(0, Math.min(ctx.canvas.height - 1, Math.round(yCss * scaleY)));

        const { data } = ctx.getImageData(px, py, 1, 1);
        const [r, g, b/*, a*/] = data;
        return rgbToHex(r, g, b); // you already have rgbToHex
    };

    const sampleBlendHexAtCssPoint = (xCss, yCss, brushDiameterCss) => {
        const ctx = mainContext.current;
        const canvasEl = mainCanvasRef.current;
        if (!ctx || !canvasEl) return null;

        const rect = canvasEl.getBoundingClientRect();
        const effectiveCssW = rect.width / scale;
        const effectiveCssH = rect.height / scale;
        const scaleX = ctx.canvas.width / effectiveCssW;
        const scaleY = ctx.canvas.height / effectiveCssH;

        const centerPx = Math.max(0, Math.min(ctx.canvas.width - 1, Math.round(xCss * scaleX)));
        const centerPy = Math.max(0, Math.min(ctx.canvas.height - 1, Math.round(yCss * scaleY)));

        const center = ctx.getImageData(centerPx, centerPy, 1, 1).data;

        const radiusCanvas = Math.max(1, Math.round((brushDiameterCss * scaleX) / 2));
        const x0 = Math.max(0, centerPx - radiusCanvas);
        const y0 = Math.max(0, centerPy - radiusCanvas);
        const x1 = Math.min(ctx.canvas.width - 1, centerPx + radiusCanvas);
        const y1 = Math.min(ctx.canvas.height - 1, centerPy + radiusCanvas);
        const w = x1 - x0 + 1;
        const h0 = y1 - y0 + 1;
        const { data } = ctx.getImageData(x0, y0, w, h0);

        // Per-channel RGB mean of the region — the same math the canvas
        // compositor uses for translucent overdraw, so the sampled color
        // matches what layering these pixels at proportional alpha would give.
        const r2 = radiusCanvas * radiusCanvas;
        let rSum = 0, gSum = 0, bSum = 0, count = 0;
        for (let py = 0; py < h0; py++) {
            const dy = (y0 + py) - centerPy;
            for (let px = 0; px < w; px++) {
                const dx = (x0 + px) - centerPx;
                if (dx * dx + dy * dy > r2) continue;
                const i = (py * w + px) * 4;
                rSum += data[i];
                gSum += data[i + 1];
                bSum += data[i + 2];
                count++;
            }
        }
        if (count === 0) return rgbToHex(center[0], center[1], center[2]);
        return rgbToHex(
            Math.round(rSum / count),
            Math.round(gSum / count),
            Math.round(bSum / count)
        );
    };

    // 1) Adjust refreshLayerButtonImages to accept a layers array
    const refreshLayerButtonImages = useCallback((layerArray) => {
        // Create an offscreen canvas to avoid flickering
        const offscreenCanvas = document.createElement('canvas');
        // Match or downscale your final thumb size — here 35x35 just as an example:
        offscreenCanvas.width = 35;
        offscreenCanvas.height = 35;
        const offscreenCtx = offscreenCanvas.getContext('2d');

        const updated = layerArray.map((layer) => {
            // Clear the offscreen before drawing each layer
            offscreenCtx.clearRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);

            // Draw the layer’s canvas into the offscreen (scaled down)
            offscreenCtx.drawImage(
                layer.context.canvas,
                0,
                0,
                offscreenCanvas.width,
                offscreenCanvas.height
            );

            // Convert the offscreen buffer to a DataURL for the layer button
            return offscreenCanvas.toDataURL();
        });

        setLayerButtonImages(updated);
    }, []);

    const moveLayerUp = (layerIndex) => {
        if (layerIndex >= layers.length - 1) return;  // already top
        resolveSelection();
        setLayers((prev) => {
            const newLayers = [...prev];
            [newLayers[layerIndex], newLayers[layerIndex + 1]] = [
                newLayers[layerIndex + 1],
                newLayers[layerIndex]
            ];
            refreshLayerButtonImages(newLayers);
            persistLayers(newLayers);
            return newLayers;
        });
        // If moving the active layer up, shift its index accordingly
        if (activeLayerIndex === layerIndex) {
            setActiveLayerIndex(layerIndex + 1);
        } else if (activeLayerIndex === layerIndex + 1) {
            setActiveLayerIndex(layerIndex);
        }
        setShowLayerMenuIndex(null);
        saveHistory();
    };

    const moveLayerDown = (layerIndex) => {
        if (layerIndex <= 0) return; // already bottom
        resolveSelection();
        setLayers((prev) => {
            const newLayers = [...prev];
            [newLayers[layerIndex], newLayers[layerIndex - 1]] = [
                newLayers[layerIndex - 1],
                newLayers[layerIndex]
            ];
            refreshLayerButtonImages(newLayers);
            persistLayers(newLayers);
            return newLayers;
        });
        // Adjust active layer index if necessary
        if (activeLayerIndex === layerIndex) {
            setActiveLayerIndex(layerIndex - 1);
        } else if (activeLayerIndex === layerIndex - 1) {
            setActiveLayerIndex(layerIndex);
        }
        setShowLayerMenuIndex(null);
        saveHistory();
    };

    const mergeLayerUp = (layerIndex) => {
        if (layerIndex >= layers.length - 1) return; // no layer above
        resolveSelection();
        setLayers((prev) => {
            const newLayers = [...prev];
            const lowerCtx = newLayers[layerIndex].context;
            const upperCtx = newLayers[layerIndex + 1].context;

            // The layer above is drawn over the merging layer
            const offscreen = document.createElement('canvas');
            offscreen.width = lowerCtx.canvas.width;
            offscreen.height = lowerCtx.canvas.height;
            const offscreenCtx = offscreen.getContext('2d');
            offscreenCtx.drawImage(lowerCtx.canvas, 0, 0);  // merging layer first
            offscreenCtx.drawImage(upperCtx.canvas, 0, 0);  // then layer above

            // Copy merged result back into the upper layer
            upperCtx.clearRect(0, 0, upperCtx.canvas.width, upperCtx.canvas.height);
            upperCtx.drawImage(offscreen, 0, 0);

            // Remove the lower layer from the array
            newLayers.splice(layerIndex, 1);
            refreshLayerButtonImages(newLayers);
            saveHistory();
            return newLayers;
        });

        // Adjust active layer index if we removed the active layer
        if (activeLayerIndex === layerIndex) {
            setActiveLayerIndex(layerIndex); // The same numeric index now refers to what used to be layerIndex+1
        } else if (activeLayerIndex > layerIndex) {
            setActiveLayerIndex(activeLayerIndex - 1);
        }
        setShowLayerMenuIndex(null);
    };

    const mergeLayerDown = (layerIndex) => {
        if (layerIndex <= 0) return; // no layer below
        resolveSelection();
        setLayers((prev) => {
            const newLayers = [...prev];
            const upperCtx = newLayers[layerIndex].context;
            const lowerCtx = newLayers[layerIndex - 1].context;

            // "The merged layer will be written over the layer below it"
            const offscreen = document.createElement('canvas');
            offscreen.width = upperCtx.canvas.width;
            offscreen.height = upperCtx.canvas.height;
            const offscreenCtx = offscreen.getContext('2d');
            offscreenCtx.drawImage(lowerCtx.canvas, 0, 0); // below first
            offscreenCtx.drawImage(upperCtx.canvas, 0, 0); // then merging layer

            lowerCtx.clearRect(0, 0, lowerCtx.canvas.width, lowerCtx.canvas.height);
            lowerCtx.drawImage(offscreen, 0, 0);

            // Remove the upper layer
            newLayers.splice(layerIndex, 1);
            refreshLayerButtonImages(newLayers);
            return newLayers;
        });

        if (activeLayerIndex === layerIndex) {
            setActiveLayerIndex(layerIndex - 1);
        } else if (activeLayerIndex > layerIndex) {
            setActiveLayerIndex(activeLayerIndex - 1);
        }
        saveHistory();
        setShowLayerMenuIndex(null);
    };


    /** ────────────────────────────── Utility / Local Storage ────────────────────────────── */

    const getLocalStorageKey = useCallback(
        (key) => `${guid}-${key}`,
        [guid]
    );

    // Draws the floating selection (cut pixels + current transform) into any
    // context. Used to composite onto mainCanvas, to bake into the layer on
    // commit, and to serialize a loss-free autosave snapshot. Defined early
    // (and stable) so persistLayers/drawMainCanvas can list it as a dep.
    const drawFloatingSelection = useCallback((ctx) => {
        const sel = selectionRef.current;
        if (!sel) return;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.translate(sel.centerX, sel.centerY);
        ctx.rotate(sel.rotation);
        ctx.scale(sel.scaleX, sel.scaleY);
        ctx.drawImage(sel.canvas, -sel.w / 2, -sel.h / 2);
        ctx.restore();
    }, []);

    // --- Color persistence (single keys) ---
    const DEFAULT_RED = 'rgba(255,0,0,1)';
    const colorHistoryKey = () => getLocalStorageKey('colorHistory');
    const selectedColorKey = () => getLocalStorageKey('selectedColor');
    const baseColorKey = () => getLocalStorageKey('baseColor');
    const canvasRatioKey = () => getLocalStorageKey('canvasRatio');

    // Persist a given layer array to localStorage immediately.
    // Used by structural mutations (add/delete) that must survive a refresh
    // even before the 5s autosave interval fires.
    const persistLayers = useCallback((targetLayers) => {
        for (let i = targetLayers.length; i < MAX_LAYERS; i++) {
            localStorage.removeItem(getLocalStorageKey(`layer${i}`));
        }
        targetLayers.forEach((layer, index) => {
            const canvas = layer.context.canvas;
            if (!canvas) return;
            let dataUrl;
            const sel = selectionRef.current;
            if (sel && index === sel.layerIndex && offScreenContext.current) {
                // A live selection means this layer has a hole where the
                // content was lifted off. Persist layer + floating content
                // composited, so a mid-selection refresh restores the drawing
                // baked in place instead of losing the lifted pixels.
                const off = offScreenContext.current;
                off.setTransform(1, 0, 0, 1, 0, 0);
                off.clearRect(0, 0, off.canvas.width, off.canvas.height);
                off.drawImage(canvas, 0, 0);
                drawFloatingSelection(off);
                dataUrl = off.canvas.toDataURL();
            } else {
                dataUrl = canvas.toDataURL();
            }
            localStorage.setItem(
                getLocalStorageKey(`layer${index}`),
                JSON.stringify({ image: dataUrl })
            );
        });
    }, [getLocalStorageKey, drawFloatingSelection]);

    const getYesterdayDateInCentralTime = () => {
        const todayInCST = new Date(
            new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })
        );
        const yesterdayInCST = new Date(todayInCST);
        yesterdayInCST.setDate(todayInCST.getDate() - 1);
        return yesterdayInCST
            .toLocaleString('en-US', { timeZone: 'America/Chicago' })
            .split(',')[0];
    };

    /** 
     * Clears all layers from localStorage, resets the date, and reloads the page. 
     */
    const clearLocalStorageAndReload = () => {
        const isLocal = window.location.hostname === 'localhost';
        if (isLocal) {
            setIsPublished(false);
            const yesterdayCST = getYesterdayDateInCentralTime();
            const lastVisitDateKey = getLocalStorageKey('lastVisitDate');
            localStorage.setItem(lastVisitDateKey, yesterdayCST);
        }

        // Drop any live selection outright — the whole drawing is being cleared.
        selectionRef.current = null;
        selectionDragRef.current = null;
        isSelectionInteractingRef.current = false;
        clearSelectionOverlay();

        // Remove ALL layer entries (up to max).
        for (let i = 0; i < MAX_LAYERS; i++) {
            localStorage.removeItem(getLocalStorageKey(`layer${i}`));
        }

        // Reset base color back to white.
        localStorage.removeItem(baseColorKey());
        setBaseColor('#ffffff');
        setDraftBaseColor('#ffffff');

        // Clear out all layer canvases
        layers.forEach((layer) => {
            layer.context.clearRect(0, 0, layer.context.canvas.width, layer.context.canvas.height);
        });
        mainContext.current.clearRect(0, 0, mainContext.current.canvas.width, mainContext.current.canvas.height);

        // Reset undo history
        setHistory([]);
        setHistoryIndex(-1);

        // Reset to a single fresh layer (so the layer buttons go back to 1)
        setLayers(() => {
            const { desiredWidth, desiredHeight } = computeCanvasSize(canvasRatio);

            const singleCanvas = document.createElement('canvas');
            singleCanvas.width = desiredWidth * 2;
            singleCanvas.height = desiredHeight * 2;
            singleCanvas.style.width = `${desiredWidth}px`;
            singleCanvas.style.height = `${desiredHeight}px`;
            const ctx = singleCanvas.getContext('2d');
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.scale(1, 1);

            return [
                {
                    canvasRef: { current: singleCanvas },
                    context: ctx
                }
            ];
        });

        // Make sure layer index is 0
        setActiveLayerIndex(0);

        // Save the blank state so the first new stroke can be undone
        saveHistory();

        // Refresh layer button images with the new single-layer array
        setTimeout(() => {
            refreshLayerButtonImages(layers);
        }, 0);
    };



    const handleSignatureSubmit = () => {
        if (signature) {
            localStorage.setItem('signature', signature);
        }
        const base64Image = getBase64Image();
        saveDrawing(base64Image, guid, true, dailySubject, signature || localSignature);
        // Flush layers to localStorage before the user can navigate away.
        // The 5s autosave timer also resets when isPublished flips below, so
        // without this call the most recent strokes (or the entire drawing,
        // if drawn quickly) can be lost on return to /canvas.
        persistLayers(layers);
        setIsPublished(true);
        setShowSignaturePrompt(false);
        // 1) Fire the Google Ads conversion here
        if (window.gtag) {
            window.gtag('event', 'conversion', { 'send_to': 'AW-968867610/nCnlCJWsv4waEJr-_s0D', 'value': 1.0, 'currency': 'USD' });
        }
        navigate('/Gallery');
    };

    useEffect(() => {
        if (!hydrated) return; // don't clobber storage on initial mount
        localStorage.setItem(colorHistoryKey(), JSON.stringify(colorHistory || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colorHistory, hydrated]);

    /** ────────────────────────────── Lifecycle / Effects ────────────────────────────── */

    useEffect(() => {
        // Keep track of whether we were previously in landscape.
        // We'll initialize it based on the current window dimensions.
        let wasLandscape = window.innerWidth > window.innerHeight;

        const handleResize = () => {
            // Only show the orientation warning on phones (not iPads/desktop).
            if (!isPhone) return;

            // Check if we are in landscape right now:
            const nowIsLandscape = window.innerWidth > window.innerHeight;

            // If we *just rotated* from portrait to landscape, alert immediately:
            if (!wasLandscape && nowIsLandscape) {
                alert('The drawing canvas is optimized for portrait orientation on mobile devices.');
            }

            // Update the previous orientation tracker.
            wasLandscape = nowIsLandscape;
        };

        window.addEventListener('resize', handleResize);

        // Run once on mount so if the user loads in landscape, it alerts immediately:
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isPhone]);

    // Clear out the "recentResize" after a bit
    useEffect(() => {
        if (recentResize) {
            const timer = setTimeout(() => {
                setRecentResize(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [recentResize]);

    /**
     * Periodically autosave layers if the canvas has changed.
     */
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!isCanvasModified) return;
            persistLayers(layers);
        }, 5000);

        return () => clearInterval(intervalId);
    }, [isPublished, isCanvasModified, layers, persistLayers]);

    useEffect(() => {
        if (!hydrated) return;
        localStorage.setItem(baseColorKey(), baseColor);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseColor, hydrated]);

    /**
     * Load signature from local storage, show daily subject, etc.
     */
    useEffect(() => {
        setShowSubject(true);
        const savedSignature = localStorage.getItem('signature');
        if (savedSignature) {
            setLocalSignature(savedSignature);
            setSignature(savedSignature);
        }
    }, [setShowSubject]);

    /**
     * Setup shareDrawing function for the "Share" button from parent.
     */
    useEffect(() => {
        const shareDrawing = async () => {
            const ctx = mainContext.current;
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);


            // Fill with the base color
            ctx.fillStyle = baseColor;
            ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

            // Draw each layer on mainCanvas
            layers.forEach((layer) => {
                ctx.drawImage(layer.context.canvas, 0, 0);
            });

            mainCanvasRef.current.toBlob(async (blob) => {
                if (!blob) return;
                const file = new File([blob], 'drawing.png', { type: 'image/png' });
                const shareData = {
                    files: [file],
                    text: "Today's Subject: " + dailySubject
                };

                if (navigator.share) {
                    try {
                        await navigator.share(shareData);
                        console.log('Drawing shared successfully');
                    } catch (error) {
                        console.error('Error sharing the drawing:', error);
                    }
                } else {
                    alert('Web Share API is not supported in your browser.');
                }
            });
        };

        myShareDrawingRef.current = shareDrawing;
        return () => {
            myShareDrawingRef.current = null;
        };
    }, [myShareDrawingRef, dailySubject, layers, baseColor]);

    /**
     * Setup saveDrawing callback for the "Save" button from parent (which triggers signature prompt).
     */
    useEffect(() => {
        mySaveDrawingRef.current = () => setShowSignaturePrompt(true);
        return () => {
            mySaveDrawingRef.current = null;
        };
    }, [mySaveDrawingRef]);

    /**
     * Prevent default behaviors on mobile devices so we can draw properly.
     */
    useEffect(() => {
        const disableDefaultBehavior = (event) => {
            const t = event.target;

            // Let these UI bits behave normally
            if (t.closest('.helpClose, .confirm-dialog, .splash, .layer-selection, .navigation, .signature, .help-modal, .hsv-jitter-modal, .layer-adjust')) {
                return;
            }

            // Allow palette interactions EXCEPT the color picker modal itself.
            if (t.closest('.palette-z-index') && !t.closest('.color-picker-modal')) {
                return;
            }
            // Inside the color picker modal: allow only explicitly marked controls
            if (t.closest('.color-picker-modal') && t.closest('.modal-interactive')) {
                return;
            }

            // Block everything else so iOS doesn’t steal the gesture
            event.preventDefault();
        };

        document.addEventListener('touchstart', disableDefaultBehavior, { passive: false });
        document.addEventListener('touchmove', disableDefaultBehavior, { passive: false });
        document.addEventListener('touchend', disableDefaultBehavior, { passive: false });
        document.addEventListener('mousedown', disableDefaultBehavior, { passive: false });
        document.addEventListener('mousemove', disableDefaultBehavior, { passive: false });
        document.addEventListener('mouseup', disableDefaultBehavior, { passive: false });

        return () => {
            document.removeEventListener('touchstart', disableDefaultBehavior);
            document.removeEventListener('touchmove', disableDefaultBehavior);
            document.removeEventListener('touchend', disableDefaultBehavior);
            document.removeEventListener('mousedown', disableDefaultBehavior);
            document.removeEventListener('mousemove', disableDefaultBehavior);
            document.removeEventListener('mouseup', disableDefaultBehavior);
        };
    }, []);

    /**
     * Ensure our PWA meta tag is set for iOS Safari.
     */
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'apple-mobile-web-app-capable';
        meta.content = 'yes';
        document.head.appendChild(meta);
        return () => {
            document.head.removeChild(meta);
        };
    }, []);

    /**
     * Initial canvas setup & load from local storage (once per day).
     */

    function countStoredLayers() {
        let layerCount = 0;
        for (let i = 0; i < MAX_LAYERS; i++) {
            const dataUrl = localStorage.getItem(getLocalStorageKey(`layer${i}`));
            if (dataUrl) {
                layerCount++;
            } else {
                // The moment we see there's no layer i, break.
                break;
            }
        }
        return layerCount;
    }

    // 3) The same load function
    function loadLayersFromLocalStorage() {
        const ctx = mainContext.current;
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.fillStyle = baseColor;
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        const loadPromises = layers.map((layer, index) => {
            return new Promise((resolve) => {
                // Get the JSON string from local storage
                const storedData = localStorage.getItem(getLocalStorageKey(`layer${index}`));
                if (!storedData) {
                    return resolve();
                }

                const parsed = JSON.parse(storedData);
                const image = parsed.image;

                // If we have an image, restore it
                if (image) {
                    const img = new Image();
                    img.onload = () => {
                        const dstW = layer.context.canvas.width;
                        const dstH = layer.context.canvas.height;
                        layer.context.clearRect(0, 0, dstW, dstH);
                        // 9-arg form rescales: if the stored bitmap was authored at
                        // a different canvas size (window resize, device change, or
                        // pre-ratio-lock drawings), stretch it to fill the current
                        // canvas instead of painting at the source's pixel size.
                        layer.context.drawImage(
                            img,
                            0, 0, img.naturalWidth, img.naturalHeight,
                            0, 0, dstW, dstH
                        );
                        // Also draw onto the main canvas
                        ctx.drawImage(layer.context.canvas, 0, 0);
                        resolve();
                    };
                    img.src = image;
                } else {
                    resolve();
                }
            });
        });

        Promise.all(loadPromises).then(() => {
            refreshLayerButtonImages(layers);
            saveHistory();
        });
    }


    // 2) A separate effect that actually calls the loader once layers is ready:
    useEffect(() => {
        if (!shouldLoad) return;
        if (layers.length === 0) return;          // not ready yet
        if (!mainContext.current) return;         // or still null
        // NOW we have our layers. Let's load them:
        loadLayersFromLocalStorage(mainContext.current);

        setShouldLoad(false);
    }, [shouldLoad, layers]);


    useEffect(() => {
        // Wait until the GUID is available before doing anything that touches
        // per-user storage. On first mount `guid` is null (GuidProvider sets it
        // in its own useEffect), so without this gate the setup would run once
        // with `guid=null` — countStoredLayers would query the wrong key and
        // return 0, baking us into a 1-layer state slot. The second pass then
        // skips re-initialization (prev.length > 0), and any extra saved layers
        // are silently dropped. The same race also affected baseColor restore.
        if (!guid) return;

        const getCurrentDateInCentralTime = () => {
            return new Date()
                .toLocaleString('en-US', { timeZone: 'America/Chicago' })
                .split(',')[0];
        };

        const clearLocalStorageDrawings = () => {

            for (let i = 0; i < MAX_LAYERS; i++) {
                localStorage.removeItem(getLocalStorageKey(`layer${i}`));
            }
            layers.forEach((layer) => {
                const ctx = layer.context;
                ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            });
            mainContext.current.clearRect(
                0,
                0,
                mainContext.current.canvas.width,
                mainContext.current.canvas.height
            );
        };


        // 0) Resolve this session's aspect ratio BEFORE sizing.
        // On a new day pick the device default; otherwise restore the locked
        // choice from the prior session (falling back to device default for
        // existing users mid-drawing on rollout day).
        const currentDateInCentralTime = getCurrentDateInCentralTime();
        const lastVisitDateKey = getLocalStorageKey('lastVisitDate');
        const lastVisitDate = localStorage.getItem(lastVisitDateKey);
        const isNewDay = !lastVisitDate || lastVisitDate !== currentDateInCentralTime;

        let activeRatio;
        if (isNewDay) {
            activeRatio = defaultRatioForDevice({ isPhone });
            localStorage.setItem(canvasRatioKey(), activeRatio);
        } else {
            const stored = localStorage.getItem(canvasRatioKey());
            activeRatio = isRatioKey(stored) ? stored : defaultRatioForDevice({ isPhone });
        }
        setCanvasRatio(activeRatio);

        // 1) Setup the sizing for all canvases at the chosen ratio
        const { desiredWidth, desiredHeight } = computeCanvasSize(activeRatio);

        const initializeCanvas = (canvas) => {
            canvas.width = desiredWidth * 2;
            canvas.height = desiredHeight * 2;
            canvas.style.width = `${desiredWidth}px`;
            canvas.style.height = `${desiredHeight}px`;
        };

        // 2) Initialize mainCanvas
        initializeCanvas(mainCanvasRef.current);
        mainContext.current = mainCanvasRef.current.getContext('2d');
        mainContext.current.scale(1, 1);

        // 3) Initialize workCanvas
        initializeCanvas(workCanvasRef.current);
        workContext.current = workCanvasRef.current.getContext('2d');
        workContext.current.scale(2, 2);

        // 4) Initialize beginDrawLayerCanvas
        initializeCanvas(beginDrawLayerCanvasRef.current);
        beginDrawLayerContext.current = beginDrawLayerCanvasRef.current.getContext('2d');
        beginDrawLayerContext.current.scale(1, 1);

        // 5) offScreenCanvas
        const offScreenCanvas = offScreenCanvasRef.current;
        initializeCanvas(offScreenCanvas);
        offScreenContext.current = offScreenCanvas.getContext('2d');
        offScreenContext.current.scale(1, 1);

        // 5b) Overlay canvas for the selection lasso/handles. Drawn in
        // bitmap pixels (identity transform) so it aligns 1:1 with mainCanvas.
        initializeCanvas(overlayCanvasRef.current);
        overlayContext.current = overlayCanvasRef.current.getContext('2d');

        // 6) Initialize layers (we do it once, if not already set)
        setLayers((prev) => {
            // If we already have some layers in state, skip re-initializing
            if (prev.length > 0) return prev;

            const numberOfStoredLayers = countStoredLayers();
            const layerAmount = numberOfStoredLayers || 1; // If none stored, default to 1.

            // By default, start with 1 layer
            const defaultLayers = [];
            for (let i = 0; i < layerAmount; i++) {
                const canvasRef = React.createRef();
                const newCanvas = document.createElement('canvas');
                initializeCanvas(newCanvas);
                const ctx = newCanvas.getContext('2d');
                ctx.lineWidth = 2;
                ctx.lineCap = 'round';
                ctx.scale(1, 1);

                defaultLayers.push({
                    canvasRef,
                    context: ctx
                });
                // Put the DOM node in the ref
                canvasRef.current = newCanvas;
            }
            return defaultLayers;
        });

        // 7) Apply new-day or same-day side effects (date already resolved above)
        if (isNewDay) {
            // New day: clear drawing-related storage
            clearLocalStorageDrawings();
            setIsPublished(false);
            localStorage.setItem(lastVisitDateKey, currentDateInCentralTime);
            setShowSplash(true);

            // 🔴 Reset color state for the new day
            localStorage.removeItem(colorHistoryKey());
            localStorage.removeItem(selectedColorKey());
            localStorage.removeItem(baseColorKey());
            setColorHistory([]);
            setBaseColor('#ffffff');
            setDraftBaseColor('#ffffff');
            handleColorChange(DEFAULT_RED, /* skipHistory */ true);
            setHydrated(true);
        } else {
            // Same day: restore color & history if present
            const savedColor = localStorage.getItem(selectedColorKey());
            if (savedColor) {
                handleColorChange(savedColor, /* skipHistory */ true);
            } else {
                handleColorChange(DEFAULT_RED, /* skipHistory */ true);
            }

            const savedHistoryJson = localStorage.getItem(colorHistoryKey());
            if (savedHistoryJson) {
                try {
                    const parsed = JSON.parse(savedHistoryJson);
                    if (Array.isArray(parsed)) setColorHistory(parsed);
                } catch { }
            }

            const savedBaseColor = localStorage.getItem(baseColorKey());
            if (savedBaseColor) {
                setBaseColor(savedBaseColor);
                setDraftBaseColor(savedBaseColor);
            }

            setShouldLoad(true);
            setHydrated(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guid, getLocalStorageKey, setIsPublished]);

    // We remove the previous effect that re-loaded from storage every time `layers` changed.
    // That way, we ONLY load once on mount (if it's the same day).

    /** ────────────────────────────── Drawing Logic ────────────────────────────── */

    const drawMainCanvas = useCallback(() => {

        const ctx = mainContext.current;

        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.fillStyle = baseColor;
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        layers.forEach((layer, i) => {
            //alert('drawing main canvas layer...' + i);
            // If this is the active layer, composite the "work" / "beginDraw" changes on top.
            // Skip the destructive rebuild while a transform gesture is active —
            // handleTransformMove has already produced the correct layer pixels,
            // and re-blitting from beginDrawLayerContext under a translated matrix
            // would wipe the second-snapshot (copy) in copy-transform mode.
            if (i === activeLayerIndex && !transformMode && !selectionMode) {
                layer.context.clearRect(0, 0, layer.context.canvas.width, layer.context.canvas.height);

                // Start from the layer’s existing drawing
                layer.context.drawImage(beginDrawLayerContext.current.canvas, 0, 0);

                // Apply brush changes
                layer.context.globalAlpha = alpha;
                if (eraseMode) {
                    layer.context.globalCompositeOperation = 'destination-out';
                }
                layer.context.drawImage(workCanvasRef.current, 0, 0);
                layer.context.globalAlpha = 1;
                layer.context.globalCompositeOperation = 'source-over';
            }
            ctx.drawImage(layer.context.canvas, 0, 0);
            // The floating selection sits directly above its (holed) layer,
            // below any higher layers. It only exists in mainCanvas output —
            // layer pixels stay untouched until commit.
            if (selectionRef.current && i === selectionRef.current.layerIndex) {
                drawFloatingSelection(ctx);
            }
        });
    }, [alpha, eraseMode, layers, activeLayerIndex, baseColor, transformMode, selectionMode, drawFloatingSelection]);


    useEffect(() => {
        if (!mainContext.current || layers.length === 0) return;
        // redraw the main canvas from the existing layers.
        const ctx = mainContext.current;
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.fillStyle = baseColor;
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        layers.forEach((layer, i) => {
            ctx.drawImage(layer.context.canvas, 0, 0);
            if (selectionRef.current && i === selectionRef.current.layerIndex) {
                drawFloatingSelection(ctx);
            }
        });

    }, [layers, baseColor, drawFloatingSelection]);


    // Returns the subset of event.touches that didn't start on a UI control
    // (toolbar, layer panel, fixed-position hold buttons, etc.). A coord-based
    // filter wouldn't work because the canvas extends visually under those
    // overlays — `Touch.target` is the only reliable discriminator.
    const getCanvasTouches = (event) => {
        if (!event.touches) return [];
        return Array.from(event.touches).filter(t => !isUIElement(t.target));
    };

    const getOffset = (event) => {
        const rect = mainCanvasRef.current.getBoundingClientRect();
        let clientX, clientY;

        const canvasTouches = getCanvasTouches(event);
        if (canvasTouches.length > 0) {
            clientX = canvasTouches[0].clientX;
            clientY = canvasTouches[0].clientY;
        } else if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        // rect.left is at the OUTER border edge, but we want offsetX relative
        // to the content (drawing) area. Subtract the canvas border so the
        // stroke lands exactly under the stylus tip — at scale=8 the 2px
        // border would otherwise show up as a ~16-screen-pixel offset.
        let offsetX = (clientX - rect.left) / scale - CANVAS_BORDER;
        const offsetY = (clientY - rect.top) / scale - CANVAS_BORDER;
        // When the canvas is mirror-flipped, the left visual edge corresponds
        // to the right internal edge — invert X so touches land where the user sees them.
        // Use clientWidth (CSS pixels) because offsetX is in CSS-pixel space here;
        // the workCanvas context already compensates for the 2× hi-DPI bitmap.
        if (flippedRef.current) offsetX = mainCanvasRef.current.clientWidth - offsetX;
        return { offsetX, offsetY };
    };

    const selectColorAtPosition = (event) => {
        const rect = mainCanvasRef.current.getBoundingClientRect();
        let clientX, clientY;
        const canvasTouches = getCanvasTouches(event);
        if (canvasTouches.length > 0) {
            clientX = canvasTouches[0].clientX;
            clientY = canvasTouches[0].clientY;
        } else if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        // Compute the touch point in content-CSS pixels first (subtracting the
        // 2px border that rect.left includes), then convert to bitmap pixels
        // via the canvas's hi-DPI ratio. Doing this in two steps keeps the
        // border-compensation correct at any zoom level.
        const cssX = (clientX - rect.left) / scale - CANVAS_BORDER;
        const cssY = (clientY - rect.top) / scale - CANVAS_BORDER;
        const dpiW = mainCanvasRef.current.width / mainCanvasRef.current.clientWidth;
        const dpiH = mainCanvasRef.current.height / mainCanvasRef.current.clientHeight;
        let offsetX = cssX * dpiW;
        const offsetY = cssY * dpiH;
        if (flippedRef.current) offsetX = mainCanvasRef.current.width - offsetX;

        const ctx = mainContext.current;
        const imageData = ctx.getImageData(offsetX, offsetY, 1, 1).data;
        const [r, g, b, a] = imageData;
        const hex = rgbaToHex(`rgba(${r},${g},${b},1)`);
        handleColorChange(hex);
    };


    const handleTransformStart = (event) => {
        if (!layers[activeLayerIndex]) return;

        // First entry into the gesture: snapshot the layer. Re-entries —
        // either when a second finger arrives (so we still need to run the
        // two-finger setup math below) or stray calls from the document-level
        // touchmove listener — must NOT refresh the snapshot, or copy-mode
        // would stamp the cumulative state every move event.
        const wasAlreadyTransforming = isTransformingRef.current;
        isTransformingRef.current = true;
        if (!wasAlreadyTransforming) {
            setIsTransforming(true);
            // Copy the current layer to 'beginDraw'
            beginDrawLayerContext.current.clearRect(
                0,
                0,
                beginDrawLayerContext.current.canvas.width,
                beginDrawLayerContext.current.canvas.height
            );
            beginDrawLayerContext.current.drawImage(
                layers[activeLayerIndex].context.canvas,
                0,
                0
            );
        }

        const rect = mainCanvasRef.current.getBoundingClientRect();

        if (event.touches && event.touches.length === 1) {
            // Single-finger => translation only
            const { offsetX, offsetY } = getOffset(event);

            setTransformStartData({
                startTouches: [], // not used for single-finger
                anchorX: 0,
                anchorY: 0,
                startDistance: 0,
                startAngle: 0,
                // We'll store these to measure movement
                startOffsetX: offsetX,
                startOffsetY: offsetY
            });
            setTransformPreview({
                translateX: 0,
                translateY: 0,
                rotation: 0,
                scale: 1,
            });
        } else if (event.touches && event.touches.length === 2) {
            // Two-finger => track pinch / rotate / move.
            // Coords must match handleTransformMove (which divides by `scale`)
            // — otherwise if the user has pinch-zoomed the canvas (scale > 1),
            // start anchor and move midpoints live in different unit systems.
            const t1 = event.touches[0];
            const t2 = event.touches[1];
            const x1 = (t1.clientX - rect.left) / scale;
            const y1 = (t1.clientY - rect.top) / scale;
            const x2 = (t2.clientX - rect.left) / scale;
            const y2 = (t2.clientY - rect.top) / scale;

            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            const dx = x2 - x1;
            const dy = y2 - y1;
            const startDistance = Math.sqrt(dx * dx + dy * dy);
            const startAngle = Math.atan2(dy, dx);

            setTransformStartData({
                startTouches: [
                    { x: x1, y: y1 },
                    { x: x2, y: y2 }
                ],
                anchorX: midX,
                anchorY: midY,
                startDistance,
                startAngle,
                startOffsetX: 0,
                startOffsetY: 0,
            });
            setTransformPreview({
                translateX: 0,
                translateY: 0,
                rotation: 0,
                scale: 1,
            });
        }
    };

    /**
     * 2) Transform-mode move:
     *   - If one finger => translation
     *   - If two fingers => pinch + rotate + translation around the midpoint
     *   - Re-draw the layer context with the updated transform in real time.
     */
    const handleTransformMove = (event) => {
        if (!isTransforming || !layers[activeLayerIndex]) return;
        event.preventDefault();

        const layer = layers[activeLayerIndex];
        const { anchorX, anchorY, startDistance, startAngle, startOffsetX, startOffsetY } = transformStartData;

        if (event.touches && event.touches.length === 1) {
            // Single-finger => track translation
            const { offsetX, offsetY } = getOffset(event);

            const deltaX = offsetX - startOffsetX;
            const deltaY = offsetY - startOffsetY;

            // Update the preview
            const newPreview = {
                translateX: deltaX,
                translateY: deltaY,
                rotation: 0,
                scale: 1,
            };
            setTransformPreview(newPreview);

            layer.context.setTransform(1, 0, 0, 1, 0, 0);
            // Re-draw
            layer.context.clearRect(0, 0, layer.context.canvas.width, layer.context.canvas.height);

            // Snap the translation to integer bitmap pixels so drawImage stays
            // pixel-aligned — sub-pixel offsets here trigger the canvas's
            // bilinear smoothing and visibly soften crisp strokes.
            // getOffset returns CSS-pixel deltas, but the layer context runs
            // in bitmap space (scale 1,1 on a 2× hi-DPI buffer), so we scale
            // by the main canvas's DPR before rounding.
            const dpr = mainCanvasRef.current.width / mainCanvasRef.current.clientWidth;
            const bitmapDeltaX = Math.round(deltaX * dpr);
            const bitmapDeltaY = Math.round(deltaY * dpr);
            layer.context.translate(bitmapDeltaX, bitmapDeltaY);
            layer.context.drawImage(beginDrawLayerContext.current.canvas, 0, 0);

            drawMainCanvas();
        }
        else if (event.touches && event.touches.length === 2) {
            // Two-finger => pinch + rotate around anchor
            const rect = mainCanvasRef.current.getBoundingClientRect();
            const t1 = event.touches[0];
            const t2 = event.touches[1];
            const x1 = (t1.clientX - rect.left) / scale;
            const y1 = (t1.clientY - rect.top) / scale;
            const x2 = (t2.clientX - rect.left) / scale;
            const y2 = (t2.clientY - rect.top) / scale;

            // New midpoint (for overall translation)
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            const dx = x2 - x1;
            const dy = y2 - y1;
            const newDistance = Math.sqrt(dx * dx + dy * dy);
            const newAngle = Math.atan2(dy, dx);

            const scaleFactor = newDistance / startDistance;
            const rotationDelta = newAngle - startAngle;

            // Also translate by movement of the anchor
            const anchorDeltaX = midX - anchorX;
            const anchorDeltaY = midY - anchorY;

            layer.context.setTransform(1, 0, 0, 1, 0, 0);
            // Re-draw
            layer.context.clearRect(0, 0, layer.context.canvas.width, layer.context.canvas.height);

            // midX/midY/anchorX/anchorY are in CSS pixels but layer.context
            // runs in bitmap space (canvas.width = 2 × CSS width). Scale by
            // DPR before translating, otherwise the anchor lands at half the
            // expected finger-midpoint position.
            const dpr2f = mainCanvasRef.current.width / mainCanvasRef.current.clientWidth;
            const midXBitmap = midX * dpr2f;
            const midYBitmap = midY * dpr2f;
            const anchorXBitmap = anchorX * dpr2f;
            const anchorYBitmap = anchorY * dpr2f;

            // Step 1: move to the new midpoint
            layer.context.translate(midXBitmap, midYBitmap);
            // Step 2: rotate
            layer.context.rotate(rotationDelta);
            // Step 3: scale
            layer.context.scale(scaleFactor, scaleFactor);
            // Step 4: move origin so the anchor lines up
            layer.context.translate(-anchorXBitmap, -anchorYBitmap);
            layer.context.drawImage(beginDrawLayerContext.current.canvas, 0, 0);
            drawMainCanvas();
        }
    };

    /**
     * 3) Transform-mode end:
     *   - The user lifts finger(s). We now have the “final” transform already drawn on the layer context.
     *   - Just finalize, record a history snapshot, etc.
     */
    const handleTransformEnd = (event) => {
        if (!isTransforming) return;
        isTransformingRef.current = false;
        setIsTransforming(false);

        const layer = layers[activeLayerIndex];
        const ctx = layer.context;

        drawMainCanvas();

        ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform matrix

        // Clear the transform preview state
        setTransformPreview({
            translateX: 0,
            translateY: 0,
            rotation: 0,
            scale: 1,
        });

        // 3) *** CRUCIAL FIX ***: 
        // Copy the updated layer pixels back into beginDrawLayerContext
        beginDrawLayerContext.current.clearRect(
            0, 0,
            beginDrawLayerContext.current.canvas.width,
            beginDrawLayerContext.current.canvas.height
        );
        beginDrawLayerContext.current.drawImage(layer.context.canvas, 0, 0);


        setIsCanvasModified(true);
        saveHistory();
        refreshLayerButtonImages(layers);
    };
    // --- END ADDED / MODIFIED CODE ---

    /** ────────────────────────────── Selection Tool ────────────────────────────── */

    // Pointer position in bitmap pixels (layer canvases are 2× CSS size).
    const getSelectionPoint = (event) => {
        const { offsetX, offsetY } = getOffset(event);
        const dpr = mainCanvasRef.current.width / mainCanvasRef.current.clientWidth;
        return { x: offsetX * dpr, y: offsetY * dpr, dpr };
    };

    const clearSelectionOverlay = () => {
        const ctx = overlayContext.current;
        if (!ctx) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    };

    const redrawSelectionOverlay = () => {
        const ctx = overlayContext.current;
        if (!ctx || !mainCanvasRef.current) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        const dpr = mainCanvasRef.current.width / mainCanvasRef.current.clientWidth;
        const k = dpr / scale; // bitmap px per screen px — keeps sizes zoom-constant

        const strokeDashed = (path) => {
            // Solid white underlay + dark dashes so the lasso reads on any
            // drawing color.
            ctx.setLineDash([]);
            ctx.strokeStyle = 'rgba(255,255,255,0.9)';
            ctx.lineWidth = 3 * k;
            ctx.stroke(path);
            ctx.setLineDash([SEL_DASH[0] * k, SEL_DASH[1] * k]);
            ctx.strokeStyle = '#111';
            ctx.lineWidth = 1.5 * k;
            ctx.stroke(path);
            ctx.setLineDash([]);
        };

        const drag = selectionDragRef.current;
        if (drag && drag.type === 'lasso') {
            const pts = drag.points;
            // Open path — the gap back to the start stays visible until the
            // pointer is close enough to close it.
            const path = new Path2D();
            path.moveTo(pts[0].x, pts[0].y);
            for (let i = 1; i < pts.length; i++) path.lineTo(pts[i].x, pts[i].y);
            strokeDashed(path);

            if (pts.length > 1) {
                const first = pts[0];
                const last = pts[pts.length - 1];
                const within =
                    pts.length >= 3 &&
                    Math.hypot(last.x - first.x, last.y - first.y) <= LASSO_CLOSE_TOL_CSS * k;
                // Preview the closing segment when releasing would close.
                if (within) {
                    const closing = new Path2D();
                    closing.moveTo(last.x, last.y);
                    closing.lineTo(first.x, first.y);
                    strokeDashed(closing);
                }
                // Dot on the start point: the target to come back to. Filled
                // dark and enlarged once releasing would close the lasso.
                ctx.beginPath();
                ctx.arc(first.x, first.y, (within ? 7 : 5) * k, 0, 2 * Math.PI);
                ctx.fillStyle = within ? '#111' : 'rgba(255,255,255,0.9)';
                ctx.fill();
                ctx.lineWidth = 1.5 * k;
                ctx.strokeStyle = within ? 'rgba(255,255,255,0.9)' : '#111';
                ctx.stroke();
            }
            return;
        }

        const sel = selectionRef.current;
        if (!sel) return;

        const corners = [
            selHandleWorld(sel, -1, -1),
            selHandleWorld(sel, 1, -1),
            selHandleWorld(sel, 1, 1),
            selHandleWorld(sel, -1, 1),
        ];
        const path = new Path2D();
        path.moveTo(corners[0].x, corners[0].y);
        for (let i = 1; i < 4; i++) path.lineTo(corners[i].x, corners[i].y);
        path.closePath();
        strokeDashed(path);

        const drawHandle = (hx, hy, size, fill = 'white') => {
            ctx.save();
            ctx.translate(hx, hy);
            ctx.rotate(sel.rotation);
            ctx.fillStyle = fill;
            ctx.strokeStyle = '#333';
            ctx.lineWidth = 1 * k;
            ctx.fillRect(-size / 2, -size / 2, size, size);
            ctx.strokeRect(-size / 2, -size / 2, size, size);
            ctx.restore();
        };

        const s = SEL_HANDLE_SIZE * k;
        SEL_HANDLES.forEach(({ ux, uy }) => {
            const p = selHandleWorld(sel, ux, uy);
            drawHandle(p.x, p.y, s);
        });
        // Center (rotate) handle last and slightly larger, as the odd one out.
        drawHandle(sel.centerX, sel.centerY, s * 1.25);

        // Copy handle: same square as the others, light yellow so it reads
        // as the odd one out.
        const cp = selCopyHandleWorld(sel);
        drawHandle(cp.x, cp.y, s, '#fff3ad');
    };

    // Classifies a pointer-down against the live selection. Handles win over
    // the interior, the nearest handle wins over overlapping neighbours (small
    // selections), and the interior test runs in local (unrotated) space.
    const hitTestSelection = (px, py, dpr, precise) => {
        const sel = selectionRef.current;
        if (!sel) return { type: 'outside' };
        const thr = (precise ? SEL_HIT_RADIUS_PRECISE : SEL_HIT_RADIUS) * dpr / scale;

        // Rotate (center), copy (halfway to the top edge), and the 8
        // perimeter handles all compete nearest-wins, so on small selections
        // where they crowd together whichever is closest to the pointer
        // wins — no dead zones.
        let best = null;
        const consider = (x, y, hit) => {
            const d = Math.hypot(px - x, py - y);
            if (d <= thr && (!best || d < best.d)) best = { d, hit };
        };
        consider(sel.centerX, sel.centerY, { type: 'rotate' });
        const pc = selCopyHandleWorld(sel);
        consider(pc.x, pc.y, { type: 'copy' });
        SEL_HANDLES.forEach((handle) => {
            const p = selHandleWorld(sel, handle.ux, handle.uy);
            consider(p.x, p.y, { type: 'scale', handle });
        });
        if (best) return best.hit;

        const local = rotVec(px - sel.centerX, py - sel.centerY, -sel.rotation);
        if (
            Math.abs(local.x / sel.scaleX) <= sel.w / 2 &&
            Math.abs(local.y / sel.scaleY) <= sel.h / 2
        ) {
            return { type: 'move' };
        }
        return { type: 'outside' };
    };

    // Straight segments, no smoothing: the overlay preview, the mask, and
    // therefore the cut and the hole must all be the identical shape, and
    // with LASSO_MIN_SEG thinning the segments are ~1 CSS px anyway.
    const buildLassoPath = (points, ox = 0, oy = 0) => {
        const path = new Path2D();
        path.moveTo(points[0].x - ox, points[0].y - oy);
        for (let i = 1; i < points.length; i++) {
            path.lineTo(points[i].x - ox, points[i].y - oy);
        }
        path.closePath();
        return path;
    };

    // The cut: lift the pixels inside the lasso path off the active layer
    // into an offscreen canvas and clear them from the layer, so the floating
    // content is the only visible instance while it's being transformed.
    //
    // Both sides go through one BINARY mask (alpha snapped to 0 or 255):
    // cut = layer · m (destination-in), hole = layer · (1 − m)
    // (destination-out). If the two edges were antialiased independently
    // (e.g. clip() for the cut, fill() for the hole), each boundary pixel's
    // coverage would be split between them — a faint halo left on the layer
    // after moving the selection, and a translucent seam on cancel, breaking
    // cancelSelection's exact-restore contract. Binary complements make
    // cancel bit-exact; the aliased edge is device-pixel scale (~half a CSS
    // px), invisible.
    const createSelectionFromLasso = (points, dpr) => {
        const layer = layers[activeLayerIndex];
        if (!layer) return;
        const layerCanvas = layer.context.canvas;

        // Integer bbox clamped to the canvas — points can go off-canvas
        // (document mousemove keeps feeding moves); off-canvas parts of the
        // path just contribute no pixels.
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const pt of points) {
            if (pt.x < minX) minX = pt.x;
            if (pt.y < minY) minY = pt.y;
            if (pt.x > maxX) maxX = pt.x;
            if (pt.y > maxY) maxY = pt.y;
        }
        const x = Math.max(0, Math.floor(minX));
        const y = Math.max(0, Math.floor(minY));
        const w = Math.min(layerCanvas.width, Math.ceil(maxX)) - x;
        const h = Math.min(layerCanvas.height, Math.ceil(maxY)) - y;
        if (w < SEL_MIN_SIZE_CSS * dpr || h < SEL_MIN_SIZE_CSS * dpr) return;

        const mask = document.createElement('canvas');
        mask.width = w;
        mask.height = h;
        const maskCtx = mask.getContext('2d');
        maskCtx.fillStyle = '#000';
        maskCtx.fill(buildLassoPath(points, x, y));
        const img = maskCtx.getImageData(0, 0, w, h);
        for (let i = 3; i < img.data.length; i += 4) {
            img.data[i] = img.data[i] >= 128 ? 255 : 0;
        }
        maskCtx.putImageData(img, 0, 0);

        const cut = document.createElement('canvas');
        cut.width = w;
        cut.height = h;
        const cutCtx = cut.getContext('2d');
        cutCtx.drawImage(layerCanvas, x, y, w, h, 0, 0, w, h);
        cutCtx.globalCompositeOperation = 'destination-in';
        cutCtx.drawImage(mask, 0, 0);

        layer.context.setTransform(1, 0, 0, 1, 0, 0);
        layer.context.globalCompositeOperation = 'destination-out';
        layer.context.drawImage(mask, x, y);
        layer.context.globalCompositeOperation = 'source-over';

        selectionRef.current = {
            canvas: cut, w, h, origX: x, origY: y, layerIndex: activeLayerIndex,
            centerX: x + w / 2, centerY: y + h / 2,
            rotation: 0, scaleX: 1, scaleY: 1, transformed: false,
        };
        // The HSV adjust target just changed from the layer to this selection.
        resetAdjustSession();
        // Visually a no-op: the floating content renders exactly over the
        // hole. No history entry and no modified flag until commit.
        drawMainCanvas();
    };

    // Bake the transformed floating content into its layer. The lift, all
    // transforms, and the bake form ONE history entry, saved here.
    const commitSelection = () => {
        const sel = selectionRef.current;
        if (!sel) return;
        const layer = layers[sel.layerIndex];
        if (layer) {
            layer.context.setTransform(1, 0, 0, 1, 0, 0);
            drawFloatingSelection(layer.context);
        }
        selectionRef.current = null;
        selectionDragRef.current = null;
        isSelectionInteractingRef.current = false;
        clearSelectionOverlay();
        setIsCanvasModified(true);
        drawMainCanvas();
        saveHistory();
        refreshLayerButtonImages(layers);
        if (isTablet && guid) {
            captureTimelapseFrame(mainCanvasRef.current, guid, getTodayCST());
        }
    };

    // Exact restore: the source region was cleared at lift time, so pasting
    // the cut pixels back at their original spot is pixel-identical to the
    // pre-selection layer. No history entry, no modified flag.
    const cancelSelection = () => {
        const sel = selectionRef.current;
        if (!sel) return;
        const layer = layers[sel.layerIndex];
        // A copy's source pixels are already baked into the layer (stamped
        // when the copy spawned), so cancelling one just discards it —
        // restoring would double-stamp.
        if (layer && !sel.isCopy) {
            layer.context.setTransform(1, 0, 0, 1, 0, 0);
            layer.context.drawImage(sel.canvas, sel.origX, sel.origY);
        }
        selectionRef.current = null;
        selectionDragRef.current = null;
        isSelectionInteractingRef.current = false;
        clearSelectionOverlay();
        // The HSV adjust target reverts from the selection to the layer.
        resetAdjustSession();
        drawMainCanvas();
    };

    // Single exit point for every "leave the selection" path (tap outside,
    // tool off, layer ops, publish): bake if the user actually transformed
    // it, otherwise restore silently without touching history.
    const resolveSelection = () => {
        const sel = selectionRef.current;
        if (!sel) return;
        if (sel.transformed) commitSelection();
        else cancelSelection();
    };

    const handleSelectionStart = (event) => {
        // A second finger means pinch-zoom: drop the in-flight drag (the
        // floating selection itself stays live) and let handleGestureChange
        // take over.
        const canvasTouches = getCanvasTouches(event);
        if (canvasTouches.length >= 2) {
            selectionDragRef.current = null;
            isSelectionInteractingRef.current = false;
            // Seed the pinch tracker like startDrawing's two-finger path does —
            // handleGestureChange diffs against these points on its first frame,
            // and without a fresh seed it would diff against a previous pinch.
            setTouchPoints([
                { x: canvasTouches[0].clientX, y: canvasTouches[0].clientY },
                { x: canvasTouches[1].clientX, y: canvasTouches[1].clientY }
            ]);
            redrawSelectionOverlay();
            return;
        }
        if (event.cancelable) event.preventDefault();
        const p = getSelectionPoint(event);

        let drag = null;
        if (selectionRef.current) {
            const sel = selectionRef.current;
            // Mouse events have no touches; Apple Pencil touches report
            // touchType 'stylus'. Both are precise pointers.
            const touch = event.touches && event.touches[0];
            const precise = !touch || touch.touchType === 'stylus';
            const hit = hitTestSelection(p.x, p.y, p.dpr, precise);
            if (hit.type === 'rotate') {
                drag = { type: 'rotate', startRotation: sel.rotation, engaged: false, refAngle: 0 };
            } else if (hit.type === 'scale') {
                drag = {
                    type: 'scale', handle: hit.handle,
                    startCenterX: sel.centerX, startCenterY: sel.centerY,
                    startRotation: sel.rotation,
                    startScaleX: sel.scaleX, startScaleY: sel.scaleY,
                };
            } else if (hit.type === 'move') {
                drag = { type: 'move', x0: p.x, y0: p.y, startCenterX: sel.centerX, startCenterY: sel.centerY };
            } else if (hit.type === 'copy') {
                // Nothing is stamped or spawned until the pointer actually
                // moves (see handleSelectionMove), so a stray tap is a no-op —
                // same deferred spirit as the rotate dead-zone.
                drag = { type: 'copy', x0: p.x, y0: p.y, spawned: false };
            } else {
                // Outside: commit-or-restore, then fall through to a fresh
                // lasso. A tap with no drag yields a 1-point lasso that's
                // dropped on release, so "tap outside to commit" and "draw
                // outside for a new selection" share this path.
                resolveSelection();
            }
        }
        if (!drag) {
            drag = { type: 'lasso', points: [{ x: p.x, y: p.y }] };
        }
        selectionDragRef.current = drag;
        isSelectionInteractingRef.current = true;
        redrawSelectionOverlay();
    };

    const handleSelectionMove = (event) => {
        const drag = selectionDragRef.current;
        if (!drag) return;
        if (getCanvasTouches(event).length >= 2) {
            selectionDragRef.current = null;
            isSelectionInteractingRef.current = false;
            redrawSelectionOverlay();
            return;
        }
        if (event.cancelable) event.preventDefault();
        const p = getSelectionPoint(event);
        let sel = selectionRef.current;

        if (drag.type === 'lasso') {
            const last = drag.points[drag.points.length - 1];
            if (Math.hypot(p.x - last.x, p.y - last.y) >= LASSO_MIN_SEG) {
                drag.points.push({ x: p.x, y: p.y });
                redrawSelectionOverlay(); // composite unchanged; overlay only
            }
            return;
        }
        if (!sel) return;

        if (drag.type === 'copy' && !drag.spawned) {
            // Tap dead-zone: don't stamp anything until real movement.
            if (Math.hypot(p.x - drag.x0, p.y - drag.y0) <= 1) return;
            // Snapshot before resolving — commit/cancel null selectionRef.
            const src = {
                canvas: sel.canvas, w: sel.w, h: sel.h,
                origX: sel.origX, origY: sel.origY, layerIndex: sel.layerIndex,
                centerX: sel.centerX, centerY: sel.centerY,
                rotation: sel.rotation, scaleX: sel.scaleX, scaleY: sel.scaleY,
            };
            // Stamp the original in place: bake + history if it was
            // transformed, pixel-exact restore if untouched. Either way the
            // original now lives in the layer and the refs are cleared.
            resolveSelection();
            // The copy becomes the new floating selection, sharing the
            // original's offscreen canvas (never mutated after creation).
            // transformed:true → every resolve path commits it; isCopy:true →
            // cancel discards it, since restoring to origX/origY would
            // double-stamp pixels that are already baked.
            sel = { ...src, transformed: true, isCopy: true };
            selectionRef.current = sel;
            // Re-arm the gesture as a plain move anchored at the current
            // point, so the copy spawns exactly over the original with no
            // jump, then fall through to the move branch for this same event.
            drag.spawned = true;
            drag.type = 'move';
            drag.x0 = p.x;
            drag.y0 = p.y;
            drag.startCenterX = src.centerX;
            drag.startCenterY = src.centerY;
            selectionDragRef.current = drag;
            isSelectionInteractingRef.current = true;
        }

        if (drag.type === 'move') {
            const dx = p.x - drag.x0;
            const dy = p.y - drag.y0;
            sel.centerX = drag.startCenterX + dx;
            sel.centerY = drag.startCenterY + dy;
            if (Math.hypot(dx, dy) > 1) sel.transformed = true;
        } else if (drag.type === 'rotate') {
            const vx = p.x - sel.centerX;
            const vy = p.y - sel.centerY;
            const dead = SEL_ROTATE_DEADZONE * p.dpr / scale;
            if (!drag.engaged) {
                // The pointer starts at the center where the angle is
                // undefined; wait until it clears the dead-zone, then measure
                // rotation from that reference angle so there's no jump.
                if (Math.hypot(vx, vy) < dead) return;
                drag.engaged = true;
                drag.refAngle = Math.atan2(vy, vx);
                return;
            }
            sel.rotation = drag.startRotation + (Math.atan2(vy, vx) - drag.refAngle);
            sel.transformed = true;
        } else if (drag.type === 'scale') {
            const { ux, uy } = drag.handle;
            const hx = ux * sel.w / 2;
            const hy = uy * sel.h / 2;
            // The opposite handle/edge is the anchor; its world position must
            // not move. Work in the rotated frame so scaling stays axis-true
            // under rotation. Corners drive both axes, edge midpoints one.
            const a0 = rotVec(-hx * drag.startScaleX, -hy * drag.startScaleY, drag.startRotation);
            const anchorX = drag.startCenterX + a0.x;
            const anchorY = drag.startCenterY + a0.y;
            const d = rotVec(p.x - anchorX, p.y - anchorY, -drag.startRotation);
            let sx = drag.startScaleX;
            let sy = drag.startScaleY;
            if (ux !== 0) sx = Math.max(SEL_MIN_SCALE, d.x / (2 * hx));
            if (uy !== 0) sy = Math.max(SEL_MIN_SCALE, d.y / (2 * hy));
            const a1 = rotVec(-hx * sx, -hy * sy, drag.startRotation);
            sel.scaleX = sx;
            sel.scaleY = sy;
            sel.centerX = anchorX - a1.x;
            sel.centerY = anchorY - a1.y;
            sel.transformed = true;
        }

        drawMainCanvas();
        redrawSelectionOverlay();
    };

    const handleSelectionEnd = (event) => {
        const drag = selectionDragRef.current;
        // Idempotent: canvas onMouseUp and the document mouseup both fire.
        if (!drag) return;
        // Touch: wait for the last canvas finger to lift.
        if (event && event.touches && getCanvasTouches(event).length > 0) return;
        selectionDragRef.current = null;
        isSelectionInteractingRef.current = false;
        if (drag.type === 'lasso') {
            // No event coords here (touchend has no touches[0]); the closure
            // test runs on the stored points.
            const dpr = mainCanvasRef.current.width / mainCanvasRef.current.clientWidth;
            const pts = drag.points;
            const first = pts[0];
            const last = pts[pts.length - 1];
            const closeThr = LASSO_CLOSE_TOL_CSS * dpr / scale;
            if (pts.length >= 3 && Math.hypot(last.x - first.x, last.y - first.y) <= closeThr) {
                createSelectionFromLasso(pts, dpr);
            }
            // else: released too far from the start — lasso cancelled.
        }
        redrawSelectionOverlay();
        // No saveHistory here — history only changes on commit.
    };

    // Keep the overlay pinned to mainCanvas's layout position (the canvas is
    // centered, so its offset varies with viewport size and ratio).
    useEffect(() => {
        const sync = () => {
            const mc = mainCanvasRef.current;
            if (!mc) return;
            setOverlayLayout((prev) =>
                prev.left === mc.offsetLeft && prev.top === mc.offsetTop
                    ? prev
                    : { left: mc.offsetLeft, top: mc.offsetTop }
            );
        };
        sync();
        window.addEventListener('resize', sync);
        return () => window.removeEventListener('resize', sync);
    }, [canvasRatio, hydrated]);

    // Handle/dash sizes are screen-constant (divided by pinch scale), so the
    // overlay re-renders when zoom changes; positional alignment follows the
    // shared CSS transform automatically.
    useEffect(() => {
        redrawSelectionOverlay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scale, flipped]);

    const startDrawing = (event) => {
        const { offsetX, offsetY } = getOffset(event);

        if (sampleMode) {
            selectColorAtPosition(event);
            setSampleMode(false);
        } else {

            // Selection tool owns the gesture: lasso or handle/move drag.
            if (selectionModeRef.current) {
                handleSelectionStart(event);
                return;
            }

            // --- ADDED / MODIFIED CODE BELOW ---
            // If we are in transform mode (or copy-transform), do NOT draw. Instead, begin transform.
            if (transformMode) {
                handleTransformStart(event);
                return;
            }
            // --- END ADDED / MODIFIED CODE ---


            // Copy the current layer to 'beginDraw'
            beginDrawLayerContext.current.clearRect(
                0,
                0,
                beginDrawLayerContext.current.canvas.width,
                beginDrawLayerContext.current.canvas.height
            );
            beginDrawLayerContext.current.drawImage(
                layers[activeLayerIndex].context.canvas,
                0,
                0
            );

            const canvasTouchesAtStart = getCanvasTouches(event);
            if (canvasTouchesAtStart.length === 2) {
                // Two fingers down = pinch/zoom gesture, not a stroke. Cancel any
                // in-progress draw state from the first finger touch — otherwise
                // a touchmove between the two touchends (or a later Pencil-hover
                // mousemove) can fire draw() with a stale isDrawing closure and
                // paint a phantom line from the first finger's start.
                setIsDrawing(false);
                isDrawingRef.current = false;
                setHasMoved(false);
                workContext.current.clearRect(
                    0, 0,
                    workCanvasRef.current.width,
                    workCanvasRef.current.height
                );
                setTouchPoints([
                    { x: canvasTouchesAtStart[0].clientX, y: canvasTouchesAtStart[0].clientY },
                    { x: canvasTouchesAtStart[1].clientX, y: canvasTouchesAtStart[1].clientY }
                ]);
                return;
            }

            if (event.cancelable) event.preventDefault();

            workContext.current.clearRect(
                0,
                0,
                workContext.current.canvas.width,
                workContext.current.canvas.height
            );
            workContext.current.beginPath();

            if (
                lineMode === 'square' ||
                lineMode === 'circle' ||
                lineMode === 'straight' ||
                lineMode === 'horizontal'
            ) {
                setLineStart({ x: offsetX, y: offsetY });
            } else {
                workContext.current.moveTo(offsetX, offsetY);
            }

            let strokeColor = color; // default: current selected color

            if (blendingMode && !eraseMode) {
                const sampledHex = sampleBlendHexAtCssPoint(offsetX, offsetY, brushSize);
                if (sampledHex) {
                    strokeColor = sampledHex;            // use only for this stroke
                }
            }

            // Anchor for any per-paint re-jittering during this stroke —
            // captured before the start-of-stroke jitter randomization so
            // successive frames orbit the user's intended hue, not a drifted one.
            strokeBaseColorRef.current = strokeColor;
            // First per-paint segment will start where the path is anchored
            // by startDrawing's moveTo: the stroke origin. `first` keeps the
            // stroke's leading edge flat — only interior junctions overlap.
            lastPaintEndRef.current = { x: offsetX, y: offsetY, first: true };

            if (!eraseMode) {
                const { h, s, v } = jitterRef.current;
                if (h || s || v) {
                    strokeColor = jitterHex(strokeColor, h, s, v);
                }
            }

            // Remember the per-stroke color so the single-dot tap path
            // (endDrawing) can fill with the same jittered/blended hue.
            tempStrokeColorRef.current = strokeColor;

            // Set styles for this stroke only
            workContext.current.strokeStyle = strokeColor;
            workContext.current.fillStyle = strokeColor;  // helpful for shapes
            workContext.current.lineWidth = brushSize;

            setLastPoint({ offsetX, offsetY });
            setIsDrawing(true);
            setIsCanvasModified(true);
        }
    };

    const draw = (event) => {
        // If transformMode (or copy-transform) is active, we skip normal drawing logic
        if (transformMode) {
            handleTransformMove(event);
            return;
        }
        if (selectionModeRef.current) {
            handleSelectionMove(event);
            return;
        }
        // Check ref first so a same-tick cancel (e.g., a gesture cancelling an
        // in-progress stroke) short-circuits before the stale state closure can.
        if (!isDrawingRef.current) return;
        if (!isDrawing) return;
        if (getCanvasTouches(event).length === 2) return;
        event.preventDefault();
        if (!isDrawing) return;

        const { offsetX, offsetY } = getOffset(event);
        const { offsetX: lastX, offsetY: lastY } = lastPoint;

        // Slow strokes emit sub-pixel moves whose direction is mostly noise,
        // which skews the flat butt-cap orientation and roughens slow lines.
        // Gate freehand segments on a minimum travel distance so every
        // committed segment has a geometrically meaningful direction. Shape
        // modes are excluded — they redraw from a fixed anchor, so per-event
        // noise doesn't accumulate and gating would only add preview lag.
        const isShapeMode =
            lineMode === 'square' || lineMode === 'circle' ||
            lineMode === 'straight' || lineMode === 'horizontal';
        if (!isShapeMode) {
            const minSegment = Math.max(2, brushSize / 6);
            if (Math.hypot(offsetX - lastX, offsetY - lastY) < minSegment) return;
        }

        const midPointX = (lastX + offsetX) / 2;
        const midPointY = (lastY + offsetY) / 2;
        setHasMoved(true);


        if (lineMode === 'square') {
            const { x: startX, y: startY } = lineStart;
            workContext.current.clearRect(0, 0, workContext.current.canvas.width, workContext.current.canvas.height);
            workContext.current.strokeRect(startX, startY, offsetX - startX, offsetY - startY);
        } else if (lineMode === 'circle') {
            const { x: startX, y: startY } = lineStart;
            const radiusX = Math.abs(offsetX - startX) / 2;
            const radiusY = Math.abs(offsetY - startY) / 2;
            const centerX = startX + (offsetX - startX) / 2;
            const centerY = startY + (offsetY - startY) / 2;
            workContext.current.clearRect(0, 0, workContext.current.canvas.width, workContext.current.canvas.height);
            workContext.current.beginPath();
            workContext.current.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
            workContext.current.stroke();
        } else if (lineMode === 'straight') {
            const { x: startX, y: startY } = lineStart;
            workContext.current.clearRect(0, 0, workContext.current.canvas.width, workContext.current.canvas.height);
            workContext.current.beginPath();
            workContext.current.moveTo(startX, startY);
            workContext.current.lineTo(offsetX, offsetY);
            workContext.current.stroke();
        } else if (lineMode === 'horizontal') {
            const { x: startX, y: startY } = lineStart;
            const deltaX = Math.abs(offsetX - startX);
            const deltaY = Math.abs(offsetY - startY);
            workContext.current.clearRect(0, 0, workContext.current.canvas.width, workContext.current.canvas.height);
            workContext.current.beginPath();
            if (deltaX > deltaY) {
                workContext.current.moveTo(startX, startY);
                workContext.current.lineTo(offsetX, startY);
            } else {
                workContext.current.moveTo(startX, startY);
                workContext.current.lineTo(startX, offsetY);
            }
            workContext.current.stroke();
        } else {
            // squiggle
            const { h, s, v, mode } = jitterRef.current;
            const perPaint =
                mode === 'paint' && !eraseMode && (h || s || v) && strokeBaseColorRef.current;
            if (perPaint) {
                // Isolate this segment so a fresh strokeStyle doesn't recolor
                // previously rasterized segments on the work canvas. Start at
                // the previous segment's end (the prior midpoint) so segments
                // join without visible gaps.
                const start = lastPaintEndRef.current || { x: lastX, y: lastY };
                let startX = start.x;
                let startY = start.y;
                // Butt-capped segments that merely abut leave a hairline of
                // half-covered AA pixels at the junction; back the start up
                // ~1px inside the previous segment so its flat end is
                // overpainted. The work canvas accumulates opaquely, so the
                // overlap is invisible at any alpha.
                if (!start.first) {
                    const dx = lastX - start.x;
                    const dy = lastY - start.y;
                    const len = Math.hypot(dx, dy);
                    if (len > 0) {
                        const back = Math.min(1, len) / len;
                        startX -= dx * back;
                        startY -= dy * back;
                    }
                }
                workContext.current.beginPath();
                workContext.current.moveTo(startX, startY);
                workContext.current.quadraticCurveTo(lastX, lastY, midPointX, midPointY);
                workContext.current.strokeStyle = jitterHex(strokeBaseColorRef.current, h, s, v);
                workContext.current.stroke();
                lastPaintEndRef.current = { x: midPointX, y: midPointY };
            } else {
                workContext.current.quadraticCurveTo(lastX, lastY, midPointX, midPointY);
                workContext.current.stroke();
            }
        }

        setLastPoint({ offsetX, offsetY });
        drawMainCanvas();
    };

    const endDrawing = (event) => {

        // --- ADDED / MODIFIED CODE BELOW ---
        // If transformMode (or copy-transform) is active, finalize the transform rather than draw
        if (transformMode) {
            handleTransformEnd(event);
            return;
        }
        if (selectionModeRef.current) {
            handleSelectionEnd(event);
            return;
        }
        if (!isDrawing) return;
        if (event.cancelable) event.preventDefault();

        const { offsetX, offsetY } = lastPoint;
        if (lineMode === 'squiggle' && !hasMoved && !recentResize) {
            // Single dot
            workContext.current.clearRect(0, 0, workContext.current.canvas.width, workContext.current.canvas.height);
            workContext.current.beginPath();
            workContext.current.arc(offsetX, offsetY, brushSize / 2, 0, 2 * Math.PI);
            workContext.current.fillStyle = tempStrokeColorRef.current ?? color;
            workContext.current.fill();
        }

        workContext.current.closePath();
        setIsDrawing(false);
        setHasMoved(false);
        drawMainCanvas();

        // Immediately clear the workCanvas so it won't re-apply old strokes
        workContext.current.clearRect(
            0, 0,
            workCanvasRef.current.width,
            workCanvasRef.current.height
        );

        saveHistory();
        refreshLayerButtonImages(layers); // << Add this line
        if (isTablet && guid) {
            captureTimelapseFrame(mainCanvasRef.current, guid, getTodayCST());
        }
    };


    const handleCombinedTouchMove = (event) => {
        if (getCanvasTouches(event).length === 2 && !transformMode) {
            handleGestureChange(event);
        } else {
            draw(event);
        }
    };

    // Keep refs pointing to the latest functions so document-level
    // listeners always call the current versions (avoids stale closures).
    endDrawingRef.current = endDrawing;
    drawRef.current = draw;
    startDrawingRef.current = startDrawing;
    handleCombinedTouchMoveRef.current = handleCombinedTouchMove;
    isDrawingRef.current = isDrawing;

    useEffect(() => {
        // Track whether a mousedown/touchstart originated on a UI element
        // so we don't accidentally start drawing when dragging sliders, etc.
        const handleDocumentMouseDown = (e) => {
            pointerDownOnUIRef.current = isUIElement(e.target);
        };
        const handleDocumentMouseUp = (e) => {
            if (endDrawingRef.current) endDrawingRef.current(e);
            pointerDownOnUIRef.current = false;
        };
        const handleDocumentMouseMove = (e) => {
            // Apple Pencil hover on iPad Pro M2+ fires synthetic mousemove with
            // the tip's coordinates. While any finger is on the screen — drawing
            // single-handed or pinching — those hover events must not drive the
            // canvas, or they'd extend the active finger stroke to the Pencil
            // tip's hover position.
            if (anyTouchActiveRef.current) return;
            if (drawRef.current) drawRef.current(e);
        };
        const handleDocumentTouchStart = (e) => {
            anyTouchActiveRef.current = e.touches.length > 0;
            pointerDownOnUIRef.current = isUIElement(e.target);
        };
        const handleDocumentTouchMove = (e) => {
            // If not drawing, check if a touch has entered the canvas area.
            // Count canvas-side touches only, so a thumb holding a
            // fixed-position UI button doesn't disqualify the canvas finger.
            // Skip when a transform gesture is already in flight — otherwise
            // every touchmove would re-fire startDrawing → handleTransformStart,
            // re-running the gesture-setup math (resets startOffsetX/Y) and
            // drifting the delta. Legitimate finger-down events still arrive
            // via the React canvas onTouchStart handler.
            if (!isDrawingRef.current && !pointerDownOnUIRef.current && !isTransformingRef.current &&
                !isSelectionInteractingRef.current &&
                mainCanvasRef.current && getCanvasTouches(e).length === 1) {
                if (startDrawingRef.current) startDrawingRef.current(e);
            }
            if (handleCombinedTouchMoveRef.current) handleCombinedTouchMoveRef.current(e);
        };
        const handleDocumentTouchEnd = (e) => {
            anyTouchActiveRef.current = e.touches.length > 0;
            if (endDrawingRef.current) endDrawingRef.current(e);
            pointerDownOnUIRef.current = false;
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);
        document.addEventListener('mouseup', handleDocumentMouseUp);
        document.addEventListener('mousemove', handleDocumentMouseMove);
        document.addEventListener('touchstart', handleDocumentTouchStart, { passive: true });
        document.addEventListener('touchmove', handleDocumentTouchMove, { passive: false });
        document.addEventListener('touchend', handleDocumentTouchEnd);
        document.addEventListener('touchcancel', handleDocumentTouchEnd);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
            document.removeEventListener('mouseup', handleDocumentMouseUp);
            document.removeEventListener('mousemove', handleDocumentMouseMove);
            document.removeEventListener('touchstart', handleDocumentTouchStart);
            document.removeEventListener('touchmove', handleDocumentTouchMove);
            document.removeEventListener('touchend', handleDocumentTouchEnd);
            document.removeEventListener('touchcancel', handleDocumentTouchEnd);
        };
    }, []);

    const handleGestureChange = (e) => {
        clearTimeout(touchHoldTimerRef.current);
        e.preventDefault();
        if (e.touches.length !== 2) return;

        const t0 = e.touches[0];
        const t1 = e.touches[1];
        const newTouches = [
            { x: t0.clientX, y: t0.clientY },
            { x: t1.clientX, y: t1.clientY }
        ];

        // Previous frame's touches (fall back to current on the first move)
        const prevTouches = touchPoints.length === 2 ? touchPoints : newTouches;

        // Midpoints (screen/client coordinates)
        const prevMid = {
            x: (prevTouches[0].x + prevTouches[1].x) / 2,
            y: (prevTouches[0].y + prevTouches[1].y) / 2
        };
        const newMid = {
            x: (newTouches[0].x + newTouches[1].x) / 2,
            y: (newTouches[0].y + newTouches[1].y) / 2
        };

        // Pinch scale between frames
        const prevDist =
            Math.hypot(prevTouches[0].x - prevTouches[1].x, prevTouches[0].y - prevTouches[1].y) || 1;
        const newDist =
            Math.hypot(newTouches[0].x - newTouches[1].x, newTouches[0].y - newTouches[1].y);
        const rawScaleChange = newDist / prevDist;

        // We'll need the element's rect to back out the untransformed base offset.
        const rect = mainCanvasRef.current.getBoundingClientRect();

        setScale((prevScale) => {
            const MIN_SCALE = 0.25;
            const MAX_SCALE = 8;
            const nextUnclamped = prevScale * rawScaleChange;
            const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextUnclamped));

            // r is the actual per-frame scale ratio after clamping.
            const r = nextScale / prevScale;

            // Update translation so that the world point under prevMid ends up under newMid.
            setPosition((prevPos) => {
                // Because transformOrigin is (0,0), rect.left/top = baseLeft/top + prevPos.x/y
                const baseLeft = rect.left - prevPos.x;
                const baseTop = rect.top - prevPos.y;

                // Convert midpoints into element-local (base) coords
                const aPrevX = prevMid.x - baseLeft;
                const aPrevY = prevMid.y - baseTop;
                const aNewX = newMid.x - baseLeft;
                const aNewY = newMid.y - baseTop;

                // Core formula:
                // pos' = r * pos + (a_new - r * a_prev)
                return {
                    x: r * prevPos.x + (aNewX - r * aPrevX),
                    y: r * prevPos.y + (aNewY - r * aPrevY)
                };
            });

            return nextScale;
        });

        setTouchPoints(newTouches);
        setRecentResize(true);
    };


    /** ────────────────────────────── Helper / Utils ────────────────────────────── */

    function getBase64Image() {
        // Bake or restore any live selection first so the exported image and
        // the layer state agree.
        resolveSelection();
        const canvas = mainCanvasRef.current;
        if (!canvas) return null;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');

        tempCtx.fillStyle = baseColor;
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        tempCtx.drawImage(canvas, 0, 0);

        return tempCanvas.toDataURL('image/jpeg');
    }

    const saveDrawing = async (
        base64Image,
        userId,
        published,
        drawingSubject,
        signature
    ) => {
        try {
            await fetch('https://ibt18xjvyj.execute-api.us-east-1.amazonaws.com', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    image: base64Image,
                    userId: userId,
                    published: published,
                    drawingSubject: drawingSubject,
                    signature: signature
                })
            });
        } catch (error) {
            console.error('Failed to save drawing:', error);
        }
    };

    const handleBrushSizeChange = (size) => {
        const sizeMap = { XS: 2, S: 5, M: 10, L: 20, XL: 40 };
        setBrushSize(sizeMap[size]);
    };

    const handleAlphaChange = (newAlpha) => {
        setAlpha(newAlpha);
    };

    const handleLineModeChange = (lineMode) => {
        setLineMode(lineMode);
    };


    const handleTransformModeChange = (transformMode) => {
        setTransformMode(transformMode);
    };

    const handleSelectionModeChange = (next) => {
        if (next) {
            // sampleMode's branch runs first in startDrawing — clear it so
            // the selection tool actually receives the gesture.
            setSampleMode(false);
            selectionModeRef.current = true;
            setSelectionMode(true);
        } else {
            // Leaving the tool resolves any live selection: bake it if it
            // was transformed, otherwise restore the pixels in place.
            resolveSelection();
            selectionModeRef.current = false;
            setSelectionMode(false);
        }
    };

    // Undo with a live selection reads as "undo the selection": restore the
    // lifted pixels in place and stop — the next tap undoes history normally.
    const handleUndoWithSelection = () => {
        if (selectionRef.current) {
            cancelSelection();
            return;
        }
        undoLastAction();
    };

    // Erase, sample, and blend are mutually exclusive brush modes: entering
    // erase or sample turns the other two off.
    const handleEraseModeChange = (next) => {
        setEraseMode(next);
        if (next) {
            setSampleMode(false);
            setBlendingMode(false);
        }
    };

    const handleSampleModeChange = (next) => {
        setSampleMode(next);
        if (next) {
            setEraseMode(false);
            setBlendingMode(false);
        }
    };

    const handleToggleFlipped = () => {
        flippedRef.current = !flippedRef.current;
        setFlipped(flippedRef.current);
    };

    // Tablet-only big-button toggles. Tap to enter the mode, then draw with the
    // stylus; tap again to leave. (Hold-to-modify isn't viable on iPadOS Safari
    // because a held finger touch suppresses concurrent Apple Pencil events.)
    // We toggle on touchend rather than click because iOS doesn't reliably
    // synthesize click events for fixed-position buttons on a page with
    // non-passive touch listeners. preventDefault suppresses any synthetic
    // click that might still fire (avoiding double-toggle on devices that do
    // synthesize one). onClick stays for mouse on desktop.
    const toggleBigErase = (e) => {
        if (e && e.cancelable) e.preventDefault();
        handleEraseModeChange(!eraseMode);
    };
    const toggleBigSample = (e) => {
        if (e && e.cancelable) e.preventDefault();
        handleSampleModeChange(!sampleMode);
    };

    // ── Layer HSV adjust: engine ──
    // Targets the floating selection when one exists (adjust just the
    // selected content), else the whole active layer. The session persists
    // across gestures: slider values are absolute offsets from the snapshot
    // taken here, and only a target change forgets it (resetAdjustSession).
    const beginAdjustSession = () => {
        if (adjustSessionRef.current) return adjustSessionRef.current;
        const sel = selectionRef.current;
        let ctx;
        if (sel) {
            ctx = sel.canvas.getContext('2d');
        } else {
            const layer = layers[activeLayerIndex];
            if (!layer) return null;
            ctx = layer.context;
            // drawMainCanvas destructively rebuilds the active layer from
            // beginDrawLayer + work (the mid-stroke invariant), so a layer
            // preview must keep that pair consistent: clear the (already
            // baked) work canvas once here, and renderAdjust mirrors every
            // frame into beginDrawLayer. Otherwise the first rebuild reverts
            // the adjustment.
            // save/restore: the work context carries a persistent 2x hi-DPI
            // scale (set at canvas setup) that stroke drawing relies on —
            // resetting it permanently would offset every later stroke.
            const wctx = workContext.current;
            wctx.save();
            wctx.setTransform(1, 0, 0, 1, 0, 0);
            wctx.clearRect(0, 0, wctx.canvas.width, wctx.canvas.height);
            wctx.restore();
        }
        const { width, height } = ctx.canvas;
        const src = ctx.getImageData(0, 0, width, height);
        // Content bounding box: every per-frame cost below (pixel loop and
        // putImageData) is restricted to it, so sparse drawings only pay for
        // the pixels they actually have.
        const s0 = src.data;
        let bx0 = width, by0 = height, bx1 = -1, by1 = -1;
        for (let y = 0; y < height; y++) {
            const row = y * width * 4 + 3;
            for (let x = 0; x < width; x++) {
                if (s0[row + x * 4]) {
                    if (x < bx0) bx0 = x;
                    if (x > bx1) bx1 = x;
                    if (y < by0) by0 = y;
                    by1 = y;
                }
            }
        }
        adjustSessionRef.current = {
            ctx,
            isSelection: !!sel,
            src,
            out: new ImageData(width, height),
            width,
            bx0, by0, bx1, by1,
            lastBaked: { h: 0, s: 0, v: 0 },
        };
        return adjustSessionRef.current;
    };

    const renderAdjust = (vals) => {
        const ses = adjustSessionRef.current;
        if (!ses || ses.bx1 < 0) return; // no session or no content
        const bw = ses.bx1 - ses.bx0 + 1;
        const bh = ses.by1 - ses.by0 + 1;
        let img;
        if (!vals.h && !vals.s && !vals.v) {
            // Identity shortcut: rgb→hsv→rgb round-trips drift ±1 from
            // rounding, so a return-to-zero must restore the snapshot verbatim.
            img = ses.src;
        } else {
            const s0 = ses.src.data;
            const d = ses.out.data;
            const dh = vals.h;
            const ds = vals.s / 100;
            const dv = vals.v / 100;
            const w4 = ses.width * 4;
            // Inline, allocation-free version of the rgbToHsv/hsvToRgb
            // helpers above — per-pixel function calls and result objects
            // made full-layer previews too slow.
            for (let y = ses.by0; y <= ses.by1; y++) {
                let i = y * w4 + ses.bx0 * 4;
                for (let x = 0; x < bw; x++, i += 4) {
                    const a = s0[i + 3];
                    if (a === 0) continue; // out starts zeroed; alpha never changes
                    const r = s0[i], g = s0[i + 1], b = s0[i + 2];
                    const mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
                    const mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
                    const df = mx - mn;
                    let h;
                    if (df === 0) h = 0;
                    else if (mx === r) h = 60 * ((g - b) / df);
                    else if (mx === g) h = 60 * ((b - r) / df) + 120;
                    else h = 60 * ((r - g) / df) + 240;
                    h = (h + dh) % 360;
                    if (h < 0) h += 360;
                    let s = mx === 0 ? 0 : df / mx;
                    s += ds;
                    if (s < 0) s = 0; else if (s > 1) s = 1;
                    let v = mx / 255 + dv;
                    if (v < 0) v = 0; else if (v > 1) v = 1;
                    const C = v * s;
                    const hh = h / 60;
                    const X = C * (1 - Math.abs((hh % 2) - 1));
                    let rr = 0, gg = 0, bb = 0;
                    if (hh < 1) { rr = C; gg = X; }
                    else if (hh < 2) { rr = X; gg = C; }
                    else if (hh < 3) { gg = C; bb = X; }
                    else if (hh < 4) { gg = X; bb = C; }
                    else if (hh < 5) { rr = X; bb = C; }
                    else { rr = C; bb = X; }
                    const m = v - C;
                    d[i] = ((rr + m) * 255 + 0.5) | 0;
                    d[i + 1] = ((gg + m) * 255 + 0.5) | 0;
                    d[i + 2] = ((bb + m) * 255 + 0.5) | 0;
                    d[i + 3] = a;
                }
            }
            img = ses.out;
        }
        ses.ctx.putImageData(img, 0, 0, ses.bx0, ses.by0, bw, bh);
        if (!ses.isSelection) {
            // Keep the rebuild sources in sync (see beginAdjustSession).
            beginDrawLayerContext.current.putImageData(img, 0, 0, ses.bx0, ses.by0, bw, bh);
        }
        drawMainCanvas();
    };

    const handleAdjustChange = (channel, value) => {
        if (!beginAdjustSession()) return;
        const next = { ...(adjustPendingRef.current || { h: 0, s: 0, v: 0 }), [channel]: value };
        adjustPendingRef.current = next;
        setAdjustVals(next);
        // One recompute per frame.
        if (!adjustRafRef.current) {
            adjustRafRef.current = requestAnimationFrame(() => {
                adjustRafRef.current = 0;
                if (adjustPendingRef.current) renderAdjust(adjustPendingRef.current);
            });
        }
    };

    // Bake on release, keeping the session and slider positions so further
    // drags keep refining the same snapshot. Multiple end events (pointerup +
    // touchend) may fire; the lastBaked comparison makes repeats no-ops.
    const bakeAdjustGesture = () => {
        const ses = adjustSessionRef.current;
        if (!ses) return;
        if (adjustRafRef.current) {
            cancelAnimationFrame(adjustRafRef.current);
            adjustRafRef.current = 0;
        }
        const vals = adjustPendingRef.current || { h: 0, s: 0, v: 0 };
        renderAdjust(vals); // flush so the final slider position is what bakes
        const lb = ses.lastBaked;
        if (vals.h === lb.h && vals.s === lb.s && vals.v === lb.v) return;
        ses.lastBaked = vals;
        if (ses.isSelection) {
            // The floating pixels changed but the layer hasn't yet — mark
            // transformed so every resolve path commits, and let the commit's
            // saveHistory record lift+adjust+bake as one entry.
            const sel = selectionRef.current;
            if (sel) sel.transformed = true;
        } else {
            setIsCanvasModified(true);
            adjustBakingRef.current = true;
            saveHistory();
            adjustBakingRef.current = false;
            refreshLayerButtonImages(layers);
        }
    };

    // Forget the session when its target changes (stroke drawn, undo, layer
    // switch, selection lifecycle, ratio change, …). Pixels were already
    // baked at every gesture end, so this only drops the snapshot and
    // re-centers the sliders. Uses only stable refs/setters so it can be a
    // dependency of saveHistory/undoLastAction without re-creating them.
    const resetAdjustSession = useCallback(() => {
        if (adjustRafRef.current) {
            cancelAnimationFrame(adjustRafRef.current);
            adjustRafRef.current = 0;
        }
        adjustSessionRef.current = null;
        adjustPendingRef.current = null;
        setAdjustVals({ h: 0, s: 0, v: 0 });
    }, []);

    // Switching layers changes the adjust target.
    useEffect(() => {
        resetAdjustSession();
    }, [activeLayerIndex, resetAdjustSession]);

    const handleClearCanvas = () => {
        setShowConfirm(true);
    };

    const handleRatioChange = (next) => {
        if (next === canvasRatio || !isRatioKey(next)) return;

        const dirty = isCanvasModified || history.length > 1;
        if (dirty && !window.confirm('Switching canvas format will clear your drawing. Continue?')) {
            return;
        }

        // Layers are wiped and rebuilt below — drop any live selection
        // outright (there is nothing left to restore onto), and any HSV
        // adjust session (its snapshot canvas is about to be discarded).
        selectionRef.current = null;
        selectionDragRef.current = null;
        isSelectionInteractingRef.current = false;
        clearSelectionOverlay();
        resetAdjustSession();

        // Persist the new ratio so it survives a refresh.
        localStorage.setItem(canvasRatioKey(), next);
        setCanvasRatio(next);

        // The persisted layer bitmaps were authored at the previous ratio;
        // wipe them so a refresh doesn't try to restore them onto a
        // differently-shaped canvas.
        for (let i = 0; i < MAX_LAYERS; i++) {
            localStorage.removeItem(getLocalStorageKey(`layer${i}`));
        }

        // Any timelapse frames captured at the previous ratio would end up
        // as frames[0] in the encoder, which uses its dimensions to size the
        // output canvas and stretches every later frame to fill it — one
        // stray landscape frame is enough to squish an entire square video.
        if (guid) {
            purgeSession(guid, getTodayCST()).catch(() => {});
            resetCaptureState();
        }

        // Resize all four system canvases. Setting canvas.width/height
        // also resets the 2D context's transform/state, so re-apply the
        // per-canvas scale conventions (workCanvas is the only one with
        // a non-identity scale).
        const { desiredWidth, desiredHeight } = computeCanvasSize(next);
        const resize = (canvas) => {
            canvas.width = desiredWidth * 2;
            canvas.height = desiredHeight * 2;
            canvas.style.width = `${desiredWidth}px`;
            canvas.style.height = `${desiredHeight}px`;
        };

        resize(mainCanvasRef.current);
        mainContext.current.scale(1, 1);

        resize(workCanvasRef.current);
        workContext.current.scale(2, 2);

        resize(beginDrawLayerCanvasRef.current);
        beginDrawLayerContext.current.scale(1, 1);

        resize(offScreenCanvasRef.current);
        offScreenContext.current.scale(1, 1);

        resize(overlayCanvasRef.current);

        // Create a fresh single layer at the new dimensions.
        const newCanvas = document.createElement('canvas');
        resize(newCanvas);
        const newCtx = newCanvas.getContext('2d');
        newCtx.lineWidth = 2;
        newCtx.lineCap = 'round';
        newCtx.scale(1, 1);

        const newLayers = [{
            canvasRef: { current: newCanvas },
            context: newCtx,
        }];
        setLayers(newLayers);
        setActiveLayerIndex(0);
        setHistory([]);
        setHistoryIndex(-1);
        setIsCanvasModified(false);

        // Repaint thumbs to show the single fresh layer.
        setTimeout(() => refreshLayerButtonImages(newLayers), 0);
    };

    const handleSpecialBrushModeChange = (specialBrushMode) => {
        setSpecialBrushMode(specialBrushMode)
    }

    const handleConfirm = () => {
        clearLocalStorageAndReload();
        //set the initial undo state.
        setShowConfirm(false);
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    const undoLastAction = useCallback(() => {
        if (historyIndex <= 0) return;  // nothing to undo

        // Undo replaces the layer canvas objects outright — a live adjust
        // session would keep writing into detached canvases.
        resetAdjustSession();

        const newHistoryIndex = historyIndex - 1;
        setHistoryIndex(newHistoryIndex);

        const { layerData, activeLayerIndex: prevActiveIndex, baseColor: prevBaseColor } = history[newHistoryIndex];

        // We'll build a new layers array with the correct canvases
        const newLayers = layerData.map((dataUrl) => {
            const newCanvas = document.createElement('canvas');
            newCanvas.width = mainCanvasRef.current.width;     // match your main canvas dimensions
            newCanvas.height = mainCanvasRef.current.height;
            const newCtx = newCanvas.getContext('2d');
            newCtx.lineWidth = 2;
            newCtx.lineCap = 'round';

            // Attach the dataUrl so we can load it
            return {
                canvasRef: { current: newCanvas },
                context: newCtx,
                dataUrl
            };
        });

        // Load each dataUrl into its new canvas context
        Promise.all(
            newLayers.map(({ dataUrl, context }) => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        context.clearRect(0, 0, context.canvas.width, context.canvas.height);
                        context.drawImage(img, 0, 0);
                        resolve();
                    };
                    img.src = dataUrl;
                });
            })
        ).then(() => {
            // Now we’ve rebuilt the correct set of layers
            setLayers(newLayers);
            setActiveLayerIndex(prevActiveIndex);

            const restoredBaseColor = typeof prevBaseColor === 'string' ? prevBaseColor : '#ffffff';
            setBaseColor(restoredBaseColor);
            setDraftBaseColor(restoredBaseColor);

            // Finally, redraw mainCanvas from these newly-restored layers
            const mainCtx = mainContext.current;
            mainCtx.clearRect(0, 0, mainCtx.canvas.width, mainCtx.canvas.height);
            mainCtx.fillStyle = restoredBaseColor;
            mainCtx.fillRect(0, 0, mainCtx.canvas.width, mainCtx.canvas.height);
            newLayers.forEach(layer => {
                mainCtx.drawImage(layer.context.canvas, 0, 0);
            });

            // Refresh layer thumbnails
            refreshLayerButtonImages(newLayers);
        });
    }, [historyIndex, history, resetAdjustSession]);



    const saveHistory = useCallback(
        (overrideLayers, overrideActiveIndex, overrideBaseColor) => {
            // Invalidation hub: any history-worthy content change (stroke,
            // transform, selection commit, layer ops, clear) makes the HSV
            // adjust session's snapshot stale. The adjuster's own bake is
            // excepted via adjustBakingRef.
            if (!adjustBakingRef.current) resetAdjustSession();
            const snapshotLayers = overrideLayers || layers;
            const snapshotIndex =
                typeof overrideActiveIndex === 'number'
                    ? overrideActiveIndex
                    : activeLayerIndex;
            const snapshotBaseColor =
                typeof overrideBaseColor === 'string'
                    ? overrideBaseColor
                    : baseColor;

            const layerData = snapshotLayers.map(layer =>
                layer.context.canvas.toDataURL()
            );

            const state = {
                layerData,
                activeLayerIndex: snapshotIndex,
                baseColor: snapshotBaseColor
            };

            setHistory(prevHistory => {
                const newHistory = [...prevHistory.slice(0, historyIndex + 1), state];
                if (newHistory.length > 20) newHistory.shift();
                return newHistory;
            });

            setHistoryIndex(prevIndex => Math.min(prevIndex + 1, 19));
        },
        [layers, activeLayerIndex, historyIndex, baseColor, resetAdjustSession]
    );

    /** ────────────────────────────── Layer Management ────────────────────────────── */

    const addLayer = () => {
        if (layers.length >= MAX_LAYERS) {
            alert(`You can only have up to ${MAX_LAYERS} layers.`);
            return;
        }
        resolveSelection();

        // 1) Create the new layer’s canvas
        const newCanvas = document.createElement('canvas');
        newCanvas.width = mainCanvasRef.current.width;
        newCanvas.height = mainCanvasRef.current.height;
        newCanvas.style.width = mainCanvasRef.current.style.width;
        newCanvas.style.height = mainCanvasRef.current.style.height;
        const ctx = newCanvas.getContext('2d');
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';

        // 2) Build the new layers array
        const newLayers = [...layers, {
            canvasRef: { current: newCanvas },
            context: ctx
        }];

        // 3) Compute the new active layer index
        const newActiveIndex = newLayers.length - 1;

        // 4) Update state
        setLayers(newLayers);
        setActiveLayerIndex(newActiveIndex);

        // 5) Clear the new layer so it’s blank
        ctx.clearRect(0, 0, newCanvas.width, newCanvas.height);

        // 6) Rebuild thumbnails so the new layer's button is blank, not stale
        refreshLayerButtonImages(newLayers);

        // 7) Now save the history with both newLayers + newActiveIndex
        saveHistory(newLayers, newActiveIndex);

        // 8) Flush to localStorage so a refresh keeps the new layer
        persistLayers(newLayers);
    };

    const deleteLayer = (layerIndex) => {
        if (layers.length <= 1) {
            alert('You must have at least one layer.');
            return;
        }
        resolveSelection();

        // 1) Build new array
        const newLayers = [...layers];
        newLayers.splice(layerIndex, 1);

        // 2) Compute new active index
        let newActiveIndex = activeLayerIndex;
        if (layerIndex === activeLayerIndex) {
            // If you delete the currently active layer, revert to layer 0
            newActiveIndex = 0;
        } else if (layerIndex < activeLayerIndex) {
            newActiveIndex = activeLayerIndex - 1;
        }

        // 3) Update state
        setLayers(newLayers);
        setActiveLayerIndex(newActiveIndex);
        setIsCanvasModified(true);
        setShowLayerMenuIndex(null);

        // 4) Save snapshot
        saveHistory(newLayers, newActiveIndex);

        // 5) Rebuild thumbnails so the indices match the post-delete layer list
        refreshLayerButtonImages(newLayers);

        // 6) Flush to localStorage so a refresh keeps the deletion
        persistLayers(newLayers);
    };

    /** ────────────────────────────── Rendering ────────────────────────────── */

    // When flipped, translate by the border-box width × scale in X so the
    // mirrored canvas occupies the same visual bounding box (scaleX is applied
    // around transformOrigin (0, 0), and that origin is the border-box
    // top-left, not the content-box top-left). offsetWidth is the border-box
    // CSS width — content + border, before our CSS scale.
    const _canvasBoxWidth = mainCanvasRef.current ? mainCanvasRef.current.offsetWidth : 0;
    const _flipShiftX = flipped ? _canvasBoxWidth * scale : 0;
    const canvasStyle = {
        border: `${CANVAS_BORDER}px solid grey`,
        margin: '10px',
        touchAction: 'none',
        backgroundColor: 'white',
        transformOrigin: '0 0',
        transform: `translate(${position.x + _flipShiftX}px, ${position.y}px) scale(${scale}) scaleX(${flipped ? -1 : 1})`
    };

    const hiddenCanvasStyle = {
        transformOrigin: '0 0',
        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
        display: 'none'
    };

    const handleSplashOkClick = () => {
        setShowSplash(false);
    };

    return (

        <div style={{ textAlign: 'center' }}>

            {/* Color History Buttons on the Left Side */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '1px',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '5px',
                    zIndex: 1
                }}
            >
                {colorHistory.map((historyColor, index) => (
                    <button
                        key={index}
                        style={{
                            width: '30px',
                            height: '30px',
                            backgroundColor: historyColor,
                            border: '1px solid #000',
                            borderRadius: '50%',
                            cursor: 'pointer'
                        }}
                        onTouchStart={() => handleColorFromHistory(historyColor)} // Use onTouchStart for mobile
                        onClick={() => handleColorFromHistory(historyColor)} // Fallback for desktop
                        title={`Select ${historyColor}`}
                    />
                ))}
            </div>
            <ColorPalette
                color={color}
                setColor={handleColorChange}
                onBrushSizeChange={handleBrushSizeChange}
                onAlphaChange={handleAlphaChange}
                onUndo={handleUndoWithSelection}
                onSelectionModeChange={handleSelectionModeChange}
                onLineModeChange={handleLineModeChange}
                eraseMode={eraseMode}
                onEraseModeChange={handleEraseModeChange}
                sampleMode={sampleMode}
                setSampleMode={handleSampleModeChange}
                onClearCanvas={handleClearCanvas}
                onSpecialBrushMode={handleSpecialBrushModeChange}
                onTransformModeChange={handleTransformModeChange}
                blendingMode={blendingMode}
                onBlendingModeChange={setBlendingMode}
                isTablet={isTablet}
                isPhone={isPhone}
                flipped={flipped}
                onToggleFlipped={handleToggleFlipped}
                canvasRatio={canvasRatio}
                onRatioChange={handleRatioChange}
                onJitterChange={handleJitterChange}
            />

            <div className="container" style={{ position: 'relative' }}>
                {/* MAIN CANVAS for final composited drawing */}
                <canvas
                    ref={mainCanvasRef}
                    style={canvasStyle}
                    onMouseDown={startDrawing}
                    onMouseEnter={(e) => {
                        if (e.buttons === 1 && !isDrawing && !pointerDownOnUIRef.current &&
                            !isSelectionInteractingRef.current) startDrawing(e);
                    }}
                    onMouseUp={endDrawing}
                    onTouchStart={startDrawing}
                />

                {/* Selection overlay: dashed lasso + transform handles.
                    A separate canvas (never part of mainCanvas pixels) so
                    publish/timelapse exports can't capture it. Shares the
                    mainCanvas transform so it tracks pan/pinch/flip 1:1. */}
                <canvas
                    ref={overlayCanvasRef}
                    style={{
                        position: 'absolute',
                        left: `${overlayLayout.left}px`,
                        top: `${overlayLayout.top}px`,
                        border: `${CANVAS_BORDER}px solid transparent`,
                        pointerEvents: 'none',
                        transformOrigin: '0 0',
                        transform: canvasStyle.transform,
                    }}
                />

                {/* Hidden helper canvases */}
                <canvas ref={workCanvasRef} style={hiddenCanvasStyle} />
                <canvas ref={beginDrawLayerCanvasRef} style={hiddenCanvasStyle} />
                <canvas ref={offScreenCanvasRef} style={hiddenCanvasStyle} />

                {/* Each layer's hidden canvas */}
                {layers.map((layer, index) => (
                    <canvas
                        key={index}
                        ref={layer.canvasRef}
                        style={hiddenCanvasStyle}
                    />
                ))}
            </div>

            <div
                className="layer-selection"
                style={{
                    position: 'fixed',
                    top: '50%',
                    right: '10px',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end', // Make sure they stay right-justified
                    margin: '0px',
                    zIndex: 1
                }}
            >
                {[...layers].reverse().map((layer, reversedIndex) => {
                    const realIndex = layers.length - 1 - reversedIndex;
                    return (
                        <div
                            key={realIndex}
                            style={{
                                position: 'relative',     // so we can absolutely position the menu
                                marginBottom: '6px',
                                width: '40px',            // lock width so the layout doesn’t shift
                                textAlign: 'right'
                            }}
                        >
                            {/* The layer button itself */}
                            <button
                                style={{
                                    // Use the layer's color as the button background
                                    // For selected layer, you can add a thicker border or other highlight
                                    border: activeLayerIndex === realIndex ? '1px solid cyan' : '1px solid grey',
                                    backgroundColor: 'white',
                                    backgroundImage: `url(${layerButtonImages[realIndex] || ''})`,
                                    // Ensure the image covers the button
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    width: '35px',
                                    height: '35px',   // give it a square shape
                                    boxSizing: 'border-box',
                                    padding: '0px'
                                }}
                                onClick={() => {

                                    if (activeLayerIndex === realIndex) {
                                        // If already active, toggle the menu
                                        setShowLayerMenuIndex((prev) => (prev === realIndex ? null : realIndex));
                                    } else {
                                        // Resolve any live selection against the
                                        // old layer before switching.
                                        resolveSelection();
                                        // Otherwise, select this layer and close any open menu
                                        setActiveLayerIndex(realIndex);
                                        setShowLayerMenuIndex(null);
                                    }
                                }}
                            >

                            </button>

                            {/* Conditionally render the horizontal menu to the left (absolute) */}
                            {
                                showLayerMenuIndex === realIndex && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: 'calc(100% + 8px)',   // a bit more breathing room
                                            top: 0,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            backgroundColor: 'white',
                                            border: '1px solid black',
                                            gap: '8px',
                                            padding: '2px',
                                            borderRadius: '10px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                        }}
                                    >
                                        <button
                                            onClick={() => moveLayerUp(realIndex)}
                                            disabled={realIndex === layers.length - 1}
                                            style={{
                                                width: 38,
                                                height: 38,
                                                fontSize: 22,
                                                lineHeight: '22px',
                                                padding: 0,
                                                borderRadius: 10,
                                                color: 'black',
                                                opacity: realIndex === layers.length - 1 ? 0.4 : 1,
                                                touchAction: 'manipulation'
                                            }}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            onClick={() => moveLayerDown(realIndex)}
                                            disabled={realIndex === 0}
                                            style={{
                                                width: 38,
                                                height: 38,
                                                fontSize: 22,
                                                lineHeight: '22px',
                                                padding: 0,
                                                borderRadius: 10,
                                                color: 'black',
                                                opacity: realIndex === 0 ? 0.4 : 1,
                                                touchAction: 'manipulation'
                                            }}
                                        >
                                            ↓
                                        </button>
                                        <button
                                            onClick={() => mergeLayerDown(realIndex)}
                                            disabled={realIndex === 0}
                                            style={{
                                                width: 38,
                                                height: 38,
                                                fontSize: 18,
                                                padding: 0,
                                                borderRadius: 10,
                                                color: 'black',
                                                opacity: realIndex === 0 ? 0.4 : 1,
                                                touchAction: 'manipulation'
                                            }}
                                        >
                                            M↓
                                        </button>
                                        <button
                                            onClick={() => mergeLayerUp(realIndex)}
                                            disabled={realIndex === layers.length - 1}
                                            style={{
                                                width: 38,
                                                height: 38,
                                                fontSize: 18,
                                                padding: 0,
                                                borderRadius: 10,
                                                color: 'black',
                                                opacity: realIndex === layers.length - 1 ? 0.4 : 1,
                                                touchAction: 'manipulation'
                                            }}
                                        >
                                            M↑
                                        </button>
                                        <button
                                            onClick={() => deleteLayer(realIndex)}
                                            style={{
                                                width: 38,
                                                height: 38,
                                                fontSize: 20,
                                                padding: 0,
                                                borderRadius: 10,
                                                color: '#b00020',
                                                touchAction: 'manipulation'
                                            }}
                                        >
                                            X
                                        </button>
                                    </div>
                                )
                            }
                        </div>
                    );
                })}

                {/* Add new layer button remains right-justified and unaffected */}
                <button
                    style={{
                        fontSize: '14px',
                        backgroundColor: 'white',
                        color: 'black',
                        border: '1px solid black',
                        marginTop: '0px',
                        width: '35px',
                        height: '35px',
                        padding: '0px'
                    }}
                    onClick={() => {
                        // Close any open layer menu
                        setShowLayerMenuIndex(null);

                        // Then add the layer
                        addLayer();
                    }}
                >+</button>

                {/* Base color swatch — sits under the + button, aligned with layer icons */}
                <button
                    aria-label="Base color"
                    style={{
                        width: '35px',
                        height: '35px',
                        padding: 0,
                        marginTop: '6px',
                        backgroundColor: baseColor,
                        border: '1px solid black',
                        boxSizing: 'border-box',
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        setShowLayerMenuIndex(null);
                        setDraftBaseColor(baseColor);
                        setBaseColorPickerOpen(true);
                    }}
                />
            </div>
            <MobileColorPicker
                open={baseColorPickerOpen}
                color={draftBaseColor}
                onChange={setDraftBaseColor}
                onClose={() => {
                    setBaseColorPickerOpen(false);
                    if (draftBaseColor !== baseColor) {
                        setBaseColor(draftBaseColor);
                        setIsCanvasModified(true);
                        saveHistory(undefined, undefined, draftBaseColor);
                    }
                }}
            />
            {
                showConfirm && (
                    <ConfirmDialog
                        message="Are you sure you want to reset the canvas?"
                        onConfirm={handleConfirm}
                        onCancel={handleCancel}
                    />
                )
            }

            {
                showSplash && (
                    <div
                        className="splash"
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            width: '80%',
                            maxWidth: '500px',
                            border: '2px solid grey',
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1000,
                            transform: 'translate(-50%, -50%)',
                            padding: '20px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <b>Welcome to The Daily Art!</b>
                        <p>
                            Everyone gets the same subject and the same tools—show us your take! 
                            Publish your drawing to unlock today’s gallery, 
                            where you can browse and rate everyone else's doodles.
                        </p>

                        Tap to start drawing! 🎨
                        <button
                            onClick={handleSplashOkClick}
                            style={{
                                fontSize: '18px', // Adjusted font size
                                padding: '10px 20px', // Adjusted padding for better appearance
                                cursor: 'pointer', // Add pointer cursor for better UX
                                marginTop: '10px', // Add margin for spacing
                            }}
                        >
                            OK
                        </button>
                    </div>
                )
            }

            {
                showSignaturePrompt && (
                    <div
                        className="signature"
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'white',
                            padding: '20px',
                            border: '1px solid black',
                            zIndex: 1001
                        }}
                    >
                        <p>Sign your work or remain anonymous.</p>
                        <input
                            type="text"
                            value={signature}
                            onChange={(e) => setSignature(e.target.value)}
                            placeholder="Enter your signature"
                            style={{
                                fontSize: '16px',
                                width: '100%',
                                padding: '1px',
                                marginBottom: '10px'
                            }}
                        />
                        <div>
                            <button onClick={handleSignatureSubmit}>Publish</button>
                        </div>
                    </div>
                )
            }

            {isTablet && (
                <div style={{
                    ...holdButtonStackStyle,
                    [handedness === 'left' ? 'right' : 'left']: '24px',
                }}>
                    <button
                        type="button"
                        style={eraseMode ? holdButtonActiveStyle : holdButtonStyle}
                        onTouchEnd={toggleBigErase}
                        onClick={toggleBigErase}
                    >
                        Erase
                    </button>
                    <button
                        type="button"
                        style={sampleMode ? holdButtonActiveStyle : holdButtonStyle}
                        onTouchEnd={toggleBigSample}
                        onClick={toggleBigSample}
                    >
                        Sample
                    </button>
                </div>
            )}

            {/* HSV adjust sliders: opposite side from the Erase/Sample stack.
                Adjusts the floating selection if one exists, else the whole
                active layer. Each drag bakes on release and springs back to 0. */}
            {isTablet && (
                <div
                    className="layer-adjust"
                    style={{
                        ...adjustStackStyle,
                        [handedness === 'left' ? 'left' : 'right']: '24px',
                    }}
                >
                    {[
                        { key: 'h', label: 'Hue', min: -180, max: 180, suffix: '°' },
                        { key: 's', label: 'Sat', min: -100, max: 100, suffix: '%' },
                        { key: 'v', label: 'Val', min: -100, max: 100, suffix: '%' },
                    ].map(({ key, label, min, max, suffix }) => (
                        <div key={key} style={{ marginBottom: key === 'v' ? 0 : '8px' }}>
                            <div style={adjustLabelStyle}>
                                {label} {adjustVals[key] > 0 ? '+' : ''}{adjustVals[key]}{suffix}
                            </div>
                            <input
                                type="range"
                                min={min}
                                max={max}
                                value={adjustVals[key]}
                                onChange={(e) => handleAdjustChange(key, Number(e.target.value))}
                                onPointerUp={bakeAdjustGesture}
                                onPointerCancel={bakeAdjustGesture}
                                onTouchEnd={bakeAdjustGesture}
                                onMouseUp={bakeAdjustGesture}
                                style={{ width: '140px', display: 'block', touchAction: 'none' }}
                            />
                        </div>
                    ))}
                </div>
            )}

        </div >
    );
};

const holdButtonStackStyle = {
    position: 'fixed',
    bottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    zIndex: 1000,
    userSelect: 'none',
    WebkitUserSelect: 'none',
};

const adjustStackStyle = {
    position: 'fixed',
    bottom: '24px',
    zIndex: 1000,
    background: 'rgba(255,255,255,0.85)',
    border: '2px solid rgba(0,0,0,0.35)',
    borderRadius: '14px',
    padding: '10px 12px',
    userSelect: 'none',
    WebkitUserSelect: 'none',
};

const adjustLabelStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#222',
    marginBottom: '2px',
};

const holdButtonStyle = {
    width: '96px',
    height: '96px',
    borderRadius: '20px',
    border: '2px solid rgba(0,0,0,0.35)',
    background: 'rgba(255,255,255,0.85)',
    color: '#222',
    fontSize: '20px',
    fontWeight: 600,
    boxShadow: '0 4px 10px rgba(0,0,0,0.18)',
    touchAction: 'manipulation',
    cursor: 'pointer',
};

const holdButtonActiveStyle = {
    ...holdButtonStyle,
    background: 'rgba(60,60,60,0.92)',
    color: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3) inset',
    transform: 'scale(0.97)',
};

export default DrawingCanvas;
